import {
  getBooks,
  getCurrentUser,
  getFollowing,
  getMisc,
  getNotesStatus,
  mergePrefs,
  updateBooksCache,
  updateCurrentUser,
  updateFollowing,
  updateMisc,
  updateNotesStatus,
} from './books';
import { apiFetch, getAppPlatform, openExternalUrl } from './platform';
import { calculateFileHash, doesFileExist, getFile, saveFile } from './indexedDB';
import { logger } from './logger';
import type {
  BookCollection,
  BookMeta,
  GoogleDriveBookState,
  GroupCollection,
  NotesGroup,
  NotesStatus,
  Person,
  Prefs,
} from '../types/book';
import {
  getLocalDriveSyncState,
  updateLocalDriveSyncState,
  type DriveDeletedBook,
  type DriveDeletedGroup,
  type LocalDriveSyncState,
} from './driveSyncState';

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';
const MANIFEST_NAME = 'wereader-sync-v1-manifest.json';
const TOKEN_REFRESH_SKEW_MS = 60_000;
const MIN_SYNC_INTERVAL_MS = 30_000;
const DRIVE_REQUEST_TIMEOUT_MS = 45_000;
const DRIVE_FILE_TRANSFER_TIMEOUT_MS = 15 * 60_000;
const DRIVE_FILE_UPLOAD_CONCURRENCY = 3;
const DRIVE_COVER_DOWNLOAD_CONCURRENCY = 4;
const DRIVE_CLOUD_STATE_PERSIST_EVERY_FILES = 5;
const DRIVE_UPLOAD_PROGRESS_LOG_INTERVAL_MS = 15_000;
const DRIVE_TOKEN_CACHE_HIT_LOG_INTERVAL_MS = 30_000;
const DRIVE_STALE_SYNC_IN_FLIGHT_MS = 2 * 60 * 60_000;
const DRIVE_MANIFEST_BOOK_UPDATES_LIMIT = 25;
const DRIVE_MANIFEST_BOOK_ACTIVITY_LIMIT = 200;
const DRIVE_SYNC_REMOTE_APPLY_REASON = 'google-drive-sync-remote-apply';

type DriveFileKind = 'epub' | 'cover' | 'notes';
export type DriveSyncProgressPhase =
  | 'starting'
  | 'status'
  | 'remote-manifest'
  | 'remote-apply'
  | 'download-covers'
  | 'local-manifest'
  | 'upload-files'
  | 'tombstones'
  | 'manifest'
  | 'complete'
  | 'error';

export type DriveSyncProgress = {
  reason?: string;
  phase: DriveSyncProgressPhase;
  current?: number;
  total?: number;
  uploadedFiles?: number;
  skippedFiles?: number;
  uploadedBytes?: number;
  currentBookId?: string;
  currentBookTitle?: string;
  currentFileKind?: DriveFileKind | 'manifest';
  currentFileSize?: number;
  elapsedMs?: number;
};

export type DriveSyncStatus = {
  configured: boolean;
  connected: boolean;
  email?: string | null;
  scope?: string | null;
  hasDriveAppDataScope?: boolean;
  connectedAt?: number | null;
  updatedAt?: number | null;
  lastError?: string | null;
};

export type DriveAccessToken = {
  accessToken: string;
  expiresIn: number;
  scope?: string;
  hasDriveAppDataScope?: boolean;
  tokenType?: string;
};

export type DriveFileRef = {
  fileId: string;
  kind: DriveFileKind | 'manifest';
  name: string;
  mimeType: string;
  size?: number;
  hash?: string;
  uploadedAt: number;
  modifiedTime?: string;
};

export type DriveManifestBook = {
  meta: BookMeta;
  epub?: DriveFileRef;
  cover?: DriveFileRef;
  notes?: DriveFileRef;
  deletedAt?: number;
};

export type DriveSyncManifestV1 = {
  version: 1;
  generatedAt: number;
  deviceId: string;
  userEmail?: string;
  prefs?: Prefs;
  following?: Person[];
  misc?: Record<string, unknown> | null;
  notesStatus?: NotesStatus;
  books: Record<string, DriveManifestBook>;
  deletedBooks: Record<string, DriveDeletedBook>;
  deletedGroups?: Record<string, DriveDeletedGroup>;
};

type DriveListedFile = {
  id: string;
  name: string;
  mimeType?: string;
  size?: string;
  modifiedTime?: string;
  appProperties?: Record<string, string>;
};

type DriveUploadResult = {
  id: string;
  name: string;
  mimeType?: string;
  size?: string;
  modifiedTime?: string;
};

type DriveSyncRuntimeState = {
  syncInFlight: Promise<DriveSyncManifestV1 | null> | null;
  lastSyncStartedAt: number;
};

type AppliedLocalTombstones = {
  manifest: DriveSyncManifestV1;
  deletedBooks: Record<string, DriveDeletedBook>;
  deletedGroups: Record<string, DriveDeletedGroup>;
};

type DriveErrorDetails = {
  code?: string | number | null;
  message?: string | null;
  reasons: string[];
  needsReconnect: boolean;
  bodyLength: number;
};

const DRIVE_SCOPE_ERROR = 'missing_drive_appdata_scope';

export class GoogleDriveReauthorizationRequiredError extends Error {
  code = 'drive_reauthorization_required';

  constructor(message = 'Google Drive authorization must be refreshed') {
    super(message);
    this.name = 'GoogleDriveReauthorizationRequiredError';
  }
}

export const isGoogleDriveReauthorizationRequiredError = (
  error: unknown,
): error is GoogleDriveReauthorizationRequiredError =>
  error instanceof GoogleDriveReauthorizationRequiredError ||
  Boolean(
    error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: unknown }).code === 'drive_reauthorization_required',
  );

export class GoogleDriveSyncCancelledError extends Error {
  code = 'drive_sync_cancelled';
  reason: string;

  constructor(reason: string, message = 'Google Drive sync was paused') {
    super(message);
    this.name = 'GoogleDriveSyncCancelledError';
    this.reason = reason;
  }
}

export const isGoogleDriveSyncCancelledError = (error: unknown): error is GoogleDriveSyncCancelledError =>
  error instanceof GoogleDriveSyncCancelledError ||
  Boolean(
    error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: unknown }).code === 'drive_sync_cancelled',
  );

class GoogleDriveResponseError extends Error {
  status: number;
  operation: string;
  label: string;

  constructor(operation: string, label: string, status: number, summary: string) {
    super(`${operation} failed: ${status}${summary ? ` ${summary}` : ''}`);
    this.name = 'GoogleDriveResponseError';
    this.status = status;
    this.operation = operation;
    this.label = label;
  }
}

const isGoogleDriveResponseErrorStatus = (
  error: unknown,
  status: number,
  operation?: string,
): error is GoogleDriveResponseError =>
  error instanceof GoogleDriveResponseError &&
  error.status === status &&
  (!operation || error.operation === operation);

let cachedAccessToken: { token: string; expiresAt: number; accountKey: string } | null = null;
let lastConnectedDriveEmail: string | null = null;
let lastTokenCacheHitLogAt = 0;

const getDriveSyncRuntimeState = (): DriveSyncRuntimeState => {
  const root = globalThis as typeof globalThis & {
    __wereaderDriveSyncRuntimeState?: DriveSyncRuntimeState;
  };
  if (!root.__wereaderDriveSyncRuntimeState) {
    root.__wereaderDriveSyncRuntimeState = {
      syncInFlight: null,
      lastSyncStartedAt: 0,
    };
  }
  return root.__wereaderDriveSyncRuntimeState;
};

const dispatchDriveEvent = (name: string, detail?: Record<string, unknown>) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

const dispatchLocalDataChanged = (reason: string) => {
  dispatchDriveEvent('wereader:local-data-changed', { reason });
};

const dispatchDriveSyncProgress = (progress: DriveSyncProgress) => {
  dispatchDriveEvent('wereader:drive-sync-progress', progress as Record<string, unknown>);
};

const normalizeEmail = (email?: string | null): string => (email || '').trim().toLowerCase();

const getDriveAccountKey = (driveEmail?: string | null): string | null => {
  const currentUserEmail = normalizeEmail(getCurrentUser()?.email);
  if (!currentUserEmail) {
    return null;
  }
  return `${currentUserEmail}:${normalizeEmail(driveEmail) || 'unknown-drive-account'}`;
};

export const clearGoogleDriveAccessTokenCache = (): void => {
  cachedAccessToken = null;
  lastTokenCacheHitLogAt = 0;
};

const getBlobName = (book: BookMeta, kind: DriveFileKind): string => {
  const safeId = book.id.replace(/[^a-zA-Z0-9._-]/g, '-');
  if (kind === 'cover') {
    return `cover-${safeId}.blob`;
  }
  if (kind === 'notes') {
    return `notes-${safeId}.json`;
  }
  const hashPart = book.hash ? `-${book.hash.slice(0, 16)}` : '';
  return `book-${safeId}${hashPart}.epub`;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const cloneBook = (book: BookMeta): BookMeta => ({
  ...book,
  settings: { ...book.settings },
  notes: { ...(book.notes || {}) },
  visibleAt: { ...(book.visibleAt || {}) },
  minutesRead: { ...(book.minutesRead || {}) },
  cloud: book.cloud
    ? {
        ...book.cloud,
        googleDrive: book.cloud.googleDrive ? { ...book.cloud.googleDrive } : undefined,
      }
    : undefined,
});

const cloneRecentTimestampRecord = <T>(
  record: { [timestamp: number]: T } | undefined,
  limit: number,
): { [timestamp: number]: T } | undefined => {
  const entries = Object.entries(record || {});
  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(
    entries
      .sort(([left], [right]) => Number(left) - Number(right))
      .slice(-limit),
  ) as { [timestamp: number]: T };
};

const createDriveManifestBookMeta = (book: BookMeta): BookMeta => {
  const cloned = cloneBook(book);
  return {
    ...cloned,
    _id: undefined,
    _rev: undefined,
    notes: cloned.notes || {},
    settings: {
      ...(cloned.settings || {}),
      updates: dedupeUpdates(cloned.settings?.updates || []).slice(-DRIVE_MANIFEST_BOOK_UPDATES_LIMIT),
    },
    visibleAt: cloneRecentTimestampRecord(cloned.visibleAt, DRIVE_MANIFEST_BOOK_ACTIVITY_LIMIT),
    cloud: undefined,
    chapterHashes: undefined,
    comparisonVersion: undefined,
    normalizedTextHash: undefined,
    coreTextHash: undefined,
    shingleFingerprint: undefined,
    normalizedSectionHashes: undefined,
    textTokenCount: undefined,
    spineItemCount: undefined,
  };
};

const hasBookNotes = (notes: NotesGroup | undefined): boolean => Object.keys(notes || {}).length > 0;

const createNotesBlob = (notes: NotesGroup): Blob =>
  new Blob([JSON.stringify(notes || {})], {
    type: 'application/json',
  });

const stableStringifyJson = (value: unknown): string =>
  JSON.stringify(value, (_key, nestedValue) => {
    if (
      nestedValue &&
      typeof nestedValue === 'object' &&
      !Array.isArray(nestedValue)
    ) {
      return Object.keys(nestedValue as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (nestedValue as Record<string, unknown>)[key];
          return acc;
        }, {});
    }
    return nestedValue;
  });

const areJsonValuesEqual = (left: unknown, right: unknown): boolean =>
  stableStringifyJson(left ?? null) === stableStringifyJson(right ?? null);

const getRemoteEpubHash = (entry: DriveManifestBook): string | undefined => entry.epub?.hash || entry.meta?.hash;

const isUnsyncedLocalEpubReplacement = (
  localBook: BookMeta | undefined,
  remoteEntry: DriveManifestBook,
): boolean => {
  const localEpubHash = localBook?.hash;
  const lastSyncedEpubHash = localBook?.cloud?.googleDrive?.hash;
  const remoteEpubHash = getRemoteEpubHash(remoteEntry);
  return Boolean(
    localEpubHash &&
      lastSyncedEpubHash &&
      remoteEpubHash &&
      localEpubHash !== remoteEpubHash &&
      lastSyncedEpubHash === remoteEpubHash,
  );
};

type DriveGroupMergeSummary = {
  localGroups: number;
  remoteGroups: number;
  mergedGroups: number;
  remoteAdded: number;
  remoteUpdated: number;
  localKept: number;
  localDeleted: number;
  remoteDeleted: number;
  staleLocalRemoved: number;
  repairedInvalidParents: number;
};

const emptyGroupMergeSummary = (): DriveGroupMergeSummary => ({
  localGroups: 0,
  remoteGroups: 0,
  mergedGroups: 0,
  remoteAdded: 0,
  remoteUpdated: 0,
  localKept: 0,
  localDeleted: 0,
  remoteDeleted: 0,
  staleLocalRemoved: 0,
  repairedInvalidParents: 0,
});

const cloneGroups = (groups?: GroupCollection): GroupCollection | undefined => {
  if (!groups) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(groups).map(([id, group]) => [
      id,
      {
        ...group,
        id: group.id || id,
      },
    ]),
  );
};

const getGroupTimestamp = (groups: GroupCollection | undefined, groupId: string): number => {
  const group = groups?.[groupId];
  return toNumber(group?.updatedAt) || toNumber(group?.createdAt) || 0;
};

const getSingleGroupTimestamp = (group: GroupCollection[string] | undefined): number =>
  toNumber(group?.updatedAt) || toNumber(group?.createdAt) || 0;

const getDeletedGroupTimestamp = (
  deletedGroups: Record<string, DriveDeletedGroup> | undefined,
  groupId: string,
): number => toNumber(deletedGroups?.[groupId]?.deletedAt) || 0;

const getLatestDeletedGroupTimestamp = (
  localDeletedGroups: Record<string, DriveDeletedGroup> | undefined,
  remoteDeletedGroups: Record<string, DriveDeletedGroup> | undefined,
  groupId: string,
): number =>
  Math.max(
    getDeletedGroupTimestamp(localDeletedGroups, groupId),
    getDeletedGroupTimestamp(remoteDeletedGroups, groupId),
  );

const mergeDeletedGroupsByNewest = (
  localDeletedGroups: Record<string, DriveDeletedGroup> | undefined,
  remoteDeletedGroups: Record<string, DriveDeletedGroup> | undefined,
): Record<string, DriveDeletedGroup> => {
  const merged = { ...(remoteDeletedGroups || {}) };
  for (const [groupId, localDeletedGroup] of Object.entries(localDeletedGroups || {})) {
    const localDeletedAt = toNumber(localDeletedGroup.deletedAt) || 0;
    const remoteDeletedAt = getDeletedGroupTimestamp(merged, groupId);
    if (!merged[groupId] || localDeletedAt >= remoteDeletedAt) {
      merged[groupId] = localDeletedGroup;
    }
  }
  return merged;
};

const getRootGroupId = (groups?: GroupCollection): string | undefined => {
  if (!groups) {
    return undefined;
  }
  return groups.root?.id || Object.values(groups).find((group) => !group.parentId)?.id;
};

const normalizeDriveGroups = (
  groups: GroupCollection | undefined,
  summary: DriveGroupMergeSummary,
): GroupCollection | undefined => {
  const normalized = cloneGroups(groups);
  if (!normalized) {
    return undefined;
  }
  const rootGroupId = getRootGroupId(normalized);
  for (const group of Object.values(normalized)) {
    if (!group.parentId) {
      continue;
    }
    if (group.parentId === group.id || !normalized[group.parentId]) {
      group.parentId = rootGroupId && rootGroupId !== group.id ? rootGroupId : undefined;
      summary.repairedInvalidParents += 1;
      continue;
    }

    const seen = new Set<string>([group.id]);
    let parentId: string | undefined = group.parentId;
    while (parentId) {
      if (seen.has(parentId)) {
        group.parentId = rootGroupId && rootGroupId !== group.id ? rootGroupId : undefined;
        summary.repairedInvalidParents += 1;
        break;
      }
      seen.add(parentId);
      parentId = normalized[parentId]?.parentId;
    }
  }
  return normalized;
};

const mergeDriveGroups = (
  localGroups: GroupCollection | undefined,
  remoteGroups: GroupCollection | undefined,
  remoteGeneratedAt: number,
  localLastSyncAt?: number,
  localDeletedGroups?: Record<string, DriveDeletedGroup>,
  remoteDeletedGroups?: Record<string, DriveDeletedGroup>,
): { groups?: GroupCollection; summary: DriveGroupMergeSummary } => {
  const summary = emptyGroupMergeSummary();
  summary.localGroups = Object.keys(localGroups || {}).length;
  summary.remoteGroups = Object.keys(remoteGroups || {}).length;

  if (!localGroups && !remoteGroups) {
    return { groups: undefined, summary };
  }
  if (!localGroups) {
    const groups = normalizeDriveGroups(remoteGroups, summary);
    summary.remoteAdded = Object.keys(groups || {}).length;
    summary.mergedGroups = Object.keys(groups || {}).length;
    return { groups, summary };
  }
  if (!remoteGroups) {
    const groups = normalizeDriveGroups(localGroups, summary);
    summary.localKept = Object.keys(groups || {}).length;
    summary.mergedGroups = Object.keys(groups || {}).length;
    return { groups, summary };
  }

  const merged: GroupCollection = {};
  const allGroupIds = new Set([...Object.keys(remoteGroups), ...Object.keys(localGroups)]);
  const remoteIsNewerThanLastLocalSync = Boolean(localLastSyncAt && remoteGeneratedAt > localLastSyncAt);
  const remoteIsNotNewerThanLastLocalSync = Boolean(localLastSyncAt && remoteGeneratedAt <= localLastSyncAt);

  for (const groupId of allGroupIds) {
    const localGroup = localGroups[groupId];
    const remoteGroup = remoteGroups[groupId];
    const latestDeletedAt = getLatestDeletedGroupTimestamp(localDeletedGroups, remoteDeletedGroups, groupId);
    if (groupId !== 'root' && latestDeletedAt > 0) {
      const localSurvivesDeletion = Boolean(localGroup && getSingleGroupTimestamp(localGroup) > latestDeletedAt);
      const remoteSurvivesDeletion = Boolean(remoteGroup && getSingleGroupTimestamp(remoteGroup) > latestDeletedAt);
      if (!localSurvivesDeletion && !remoteSurvivesDeletion) {
        if (getDeletedGroupTimestamp(localDeletedGroups, groupId) >= getDeletedGroupTimestamp(remoteDeletedGroups, groupId)) {
          summary.localDeleted += 1;
        } else {
          summary.remoteDeleted += 1;
        }
        continue;
      }
    }

    if (localGroup && remoteGroup) {
      const localUpdatedAt = getGroupTimestamp(localGroups, groupId);
      const remoteUpdatedAt = getGroupTimestamp(remoteGroups, groupId);
      if (remoteUpdatedAt >= localUpdatedAt) {
        merged[groupId] = { ...remoteGroup };
        if (JSON.stringify(localGroup) !== JSON.stringify(remoteGroup)) {
          summary.remoteUpdated += 1;
        }
      } else {
        merged[groupId] = { ...localGroup };
        summary.localKept += 1;
      }
      continue;
    }

    if (remoteGroup) {
      const remoteUpdatedAt = getGroupTimestamp(remoteGroups, groupId);
      const shouldTreatAsLocalDeletion =
        groupId !== 'root' &&
        remoteIsNotNewerThanLastLocalSync &&
        remoteUpdatedAt <= (localLastSyncAt || 0);
      if (shouldTreatAsLocalDeletion) {
        summary.localDeleted += 1;
        continue;
      }
      merged[groupId] = { ...remoteGroup };
      summary.remoteAdded += 1;
      continue;
    }

    if (localGroup) {
      const localUpdatedAt = getGroupTimestamp(localGroups, groupId);
      const shouldTreatAsRemoteDeletion =
        groupId !== 'root' &&
        remoteIsNewerThanLastLocalSync &&
        localUpdatedAt > 0 &&
        localUpdatedAt <= (localLastSyncAt || 0);
      if (shouldTreatAsRemoteDeletion) {
        summary.staleLocalRemoved += 1;
      } else {
        merged[groupId] = { ...localGroup };
        summary.localKept += 1;
      }
    }
  }

  const groups = normalizeDriveGroups(merged, summary);
  summary.mergedGroups = Object.keys(groups || {}).length;
  return { groups, summary };
};

const mergePrefsForDrive = (
  localPrefs: Partial<Prefs> | undefined,
  remotePrefs: Partial<Prefs> | undefined,
  remoteGeneratedAt: number,
  localLastSyncAt?: number,
  localDeletedGroups?: Record<string, DriveDeletedGroup>,
  remoteDeletedGroups?: Record<string, DriveDeletedGroup>,
): { prefs: Prefs; groupSummary: DriveGroupMergeSummary } => {
  const sanitizePrefs = (prefs: Partial<Prefs> | undefined): Partial<Prefs> => {
    const sanitized = { ...(prefs || {}) } as Partial<Prefs> & Record<string, unknown>;
    delete sanitized._id;
    delete sanitized._rev;
    return sanitized;
  };
  const sanitizedLocalPrefs = sanitizePrefs(localPrefs);
  const sanitizedRemotePrefs = sanitizePrefs(remotePrefs);
  // The manifest timestamp and this device's last successful sync provide the
  // only shared ordering for account-level preferences. A fresh install, or a
  // manifest changed by another device since our last sync, should receive the
  // remote values. Otherwise local values remain authoritative for upload.
  const remotePrefsAreNewer = !localLastSyncAt || remoteGeneratedAt > localLastSyncAt;
  const prefs = remotePrefsAreNewer
    ? mergePrefs(sanitizedLocalPrefs, sanitizedRemotePrefs)
    : mergePrefs(sanitizedRemotePrefs, sanitizedLocalPrefs);
  const { groups, summary } = mergeDriveGroups(
    sanitizedLocalPrefs.groups,
    sanitizedRemotePrefs.groups,
    remoteGeneratedAt,
    localLastSyncAt,
    localDeletedGroups,
    remoteDeletedGroups,
  );
  if (groups) {
    prefs.groups = groups;
  }
  const rootGroupId = getRootGroupId(groups);
  const remoteActiveGroupId = sanitizedRemotePrefs.activeGroupId;
  if (prefs.groups && prefs.activeGroupId && !prefs.groups[prefs.activeGroupId]) {
    prefs.activeGroupId =
      remoteActiveGroupId && prefs.groups[remoteActiveGroupId] ? remoteActiveGroupId : rootGroupId;
  }
  return { prefs, groupSummary: summary };
};

const repairBookParentIdsForDrive = (
  books: BookCollection,
  groups: GroupCollection | undefined,
  timestamp: number,
): number => {
  if (!groups) {
    return 0;
  }
  const rootGroupId = getRootGroupId(groups);
  let repaired = 0;
  for (const [bookId, book] of Object.entries(books)) {
    if (!book || book.location === 'trash') {
      continue;
    }
    if (book.parentId && groups[book.parentId]) {
      continue;
    }
    const nextParentId = rootGroupId;
    if (book.parentId === nextParentId) {
      continue;
    }
    books[bookId] = {
      ...book,
      parentId: nextParentId,
      parentUpdatedAt: timestamp,
    };
    repaired += 1;
  }
  return repaired;
};

function dedupeUpdates(updates: NonNullable<BookMeta['settings']['updates']>) {
  const seen = new Set<number>();
  return updates.filter((update) => {
    if (seen.has(update[0])) {
      return false;
    }
    seen.add(update[0]);
    return true;
  }).sort((left, right) => left[0] - right[0]);
}

const mergeNotes = (older: NotesGroup = {}, newer: NotesGroup = {}): NotesGroup => {
  const merged: NotesGroup = { ...older };
  for (const [noteId, note] of Object.entries(newer || {})) {
    const existing = merged[noteId];
    if (!existing) {
      merged[noteId] = note;
      continue;
    }
    merged[noteId] = {
      ...existing,
      ...note,
      comments: {
        ...(existing.comments || {}),
        ...(note.comments || {}),
      },
      reactions: {
        ...(existing.reactions || {}),
        ...(note.reactions || {}),
      },
    };
  }
  return merged;
};

export const mergeBookForDrive = (localBook: BookMeta | undefined, remoteBook: BookMeta): BookMeta => {
  if (!localBook) {
    return cloneBook(remoteBook);
  }

  const localTimestamp = localBook.timestamp || localBook.lastUpdated || 0;
  const remoteTimestamp = remoteBook.timestamp || remoteBook.lastUpdated || 0;
  const base = remoteTimestamp > localTimestamp ? remoteBook : localBook;
  const overlay = remoteTimestamp > localTimestamp ? localBook : remoteBook;
  const latestCover = (localBook.coverUpdatedAt || 0) > (remoteBook.coverUpdatedAt || 0) ? localBook : remoteBook;

  return {
    ...overlay,
    ...base,
    ...((localBook.coverUpdatedAt || remoteBook.coverUpdatedAt) && {
      coverUpdatedAt: latestCover.coverUpdatedAt,
      coverImageHash: latestCover.coverImageHash,
    }),
    settings: {
      ...overlay.settings,
      ...base.settings,
      updates: dedupeUpdates([...(base.settings?.updates || []), ...(overlay.settings?.updates || [])]),
    },
    notes: mergeNotes(overlay.notes, base.notes),
    visibleAt: {
      ...overlay.visibleAt,
      ...base.visibleAt,
    },
    minutesRead: {
      ...overlay.minutesRead,
      ...base.minutesRead,
    },
    cloud: {
      ...overlay.cloud,
      ...base.cloud,
      googleDrive: {
        provider: 'google-drive',
        ...overlay.cloud?.googleDrive,
        ...base.cloud?.googleDrive,
      },
    },
  };
};

const getBookParentTimestamp = (book: BookMeta | undefined): number =>
  toNumber(book?.parentUpdatedAt) || 0;

const resolveBookParentIdForDrive = (
  localBook: BookMeta | undefined,
  remoteBook: BookMeta,
  mergedBook: BookMeta,
  remoteGeneratedAt: number,
  localLastSyncAt?: number,
): { parentId: string | undefined; parentUpdatedAt: number | undefined; source: 'local' | 'remote' | 'merged' } => {
  if (!localBook || localBook.parentId === remoteBook.parentId) {
    return {
      parentId: remoteBook.parentId,
      parentUpdatedAt: remoteBook.parentUpdatedAt || localBook?.parentUpdatedAt,
      source: 'remote',
    };
  }

  const localParentUpdatedAt = getBookParentTimestamp(localBook);
  const remoteParentUpdatedAt = getBookParentTimestamp(remoteBook);
  if (localParentUpdatedAt || remoteParentUpdatedAt) {
    if (remoteParentUpdatedAt >= localParentUpdatedAt) {
      return {
        parentId: remoteBook.parentId,
        parentUpdatedAt: remoteBook.parentUpdatedAt,
        source: 'remote',
      };
    }
    return {
      parentId: localBook.parentId,
      parentUpdatedAt: localBook.parentUpdatedAt,
      source: 'local',
    };
  }

  if (localLastSyncAt && remoteGeneratedAt > localLastSyncAt) {
    return {
      parentId: remoteBook.parentId,
      parentUpdatedAt: remoteBook.parentUpdatedAt,
      source: 'remote',
    };
  }

  return {
    parentId: mergedBook.parentId,
    parentUpdatedAt: mergedBook.parentUpdatedAt,
    source: 'merged',
  };
};

const mergeNotesStatus = (localStatus: NotesStatus = {}, remoteStatus: NotesStatus = {}): NotesStatus => {
  const merged: NotesStatus = { ...remoteStatus };
  for (const [noteId, status] of Object.entries(localStatus)) {
    const remote = merged[noteId];
    if (!remote || (status.timestamp || 0) >= (remote.timestamp || 0)) {
      merged[noteId] = status;
    }
  }
  return merged;
};

const mergeFollowing = (localFollowing: Person[] | null, remoteFollowing?: Person[]): Person[] | undefined => {
  if (!localFollowing && !remoteFollowing) {
    return undefined;
  }
  const merged = new Map<string, Person>();
  for (const person of remoteFollowing || []) {
    if (person?.email) {
      merged.set(person.email, person);
    }
  }
  for (const person of localFollowing || []) {
    if (person?.email) {
      merged.set(person.email, { ...merged.get(person.email), ...person });
    }
  }
  return Array.from(merged.values());
};

const readResponseText = async (response: Response): Promise<string> => {
  try {
    return await response.text();
  } catch {
    return '';
  }
};

const parseJsonText = (text: string): Record<string, any> | null => {
  if (!text) {
    return null;
  }
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const extractDriveErrorDetails = (text: string): DriveErrorDetails => {
  const payload = parseJsonText(text);
  const errorPayload = payload?.error && typeof payload.error === 'object' ? payload.error : payload;
  const nestedErrors = Array.isArray(errorPayload?.errors) ? errorPayload.errors : [];
  const reasons = nestedErrors
    .map((entry: any) => entry?.reason)
    .filter((reason: unknown): reason is string => typeof reason === 'string' && reason.length > 0);
  const code =
    typeof payload?.code === 'string' || typeof payload?.code === 'number'
      ? payload.code
      : typeof errorPayload?.code === 'string' || typeof errorPayload?.code === 'number'
        ? errorPayload.code
        : typeof payload?.error === 'string'
          ? payload.error
          : null;
  const message =
    typeof errorPayload?.message === 'string'
      ? errorPayload.message
      : typeof payload?.error_description === 'string'
        ? payload.error_description
        : typeof payload?.error === 'string'
          ? payload.error
          : text.slice(0, 500) || null;

  return {
    code,
    message,
    reasons,
    needsReconnect: Boolean(payload?.needsReconnect || code === DRIVE_SCOPE_ERROR),
    bodyLength: text.length,
  };
};

const isDriveReauthorizationErrorDetails = (status: number, details: DriveErrorDetails): boolean => {
  if (details.needsReconnect || details.code === DRIVE_SCOPE_ERROR) {
    return true;
  }
  if (status === 401) {
    return true;
  }
  const searchable = [details.code, details.message, ...details.reasons].filter(Boolean).join(' ').toLowerCase();
  return status === 403 && (searchable.includes('insufficient') || searchable.includes('scope'));
};

const formatDriveErrorSummary = (details: DriveErrorDetails): string => {
  const pieces = [details.code, details.reasons.join(','), details.message].filter(Boolean);
  return pieces.join(' ');
};

const redactDriveUrlForLog = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return 'unparseable-drive-url';
  }
};

const isAbortError = (error: unknown): boolean =>
  typeof DOMException !== 'undefined' && error instanceof DOMException
    ? error.name === 'AbortError'
    : Boolean(error && typeof error === 'object' && 'name' in error && (error as { name?: unknown }).name === 'AbortError');

const isDocumentHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

const fetchWithTimeout = async (
  url: string,
  init: RequestInit = {},
  timeoutMs = DRIVE_REQUEST_TIMEOUT_MS,
  label = 'drive-request',
): Promise<Response> => {
  const startedAt = Date.now();
  const method = init.method || 'GET';
  const safeUrl = redactDriveUrlForLog(url);
  const controller = new AbortController();
  let abortReason: 'timeout' | 'document-hidden' | null = null;
  const abortRequest = (reason: 'timeout' | 'document-hidden') => {
    if (controller.signal.aborted) {
      return;
    }
    abortReason = reason;
    controller.abort();
  };
  const timeoutId = globalThis.setTimeout(() => abortRequest('timeout'), timeoutMs);
  const handleVisibilityChange = () => {
    if (isDocumentHidden()) {
      abortRequest('document-hidden');
    }
  };

  if (isDocumentHidden()) {
    globalThis.clearTimeout(timeoutId);
    throw new GoogleDriveSyncCancelledError('document-hidden', `Google Drive request ${label} paused while app is hidden`);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  logger.debug('Google Drive request started', {
    label,
    method,
    url: safeUrl,
    timeoutMs,
  });

  try {
    const response = await fetch(url, {
      ...init,
      signal: controller.signal,
    });
    logger.debug('Google Drive request finished', {
      label,
      method,
      url: safeUrl,
      status: response.status,
      ok: response.ok,
      elapsedMs: Date.now() - startedAt,
    });
    return response;
  } catch (error) {
    const aborted = isAbortError(error);
    if (aborted && abortReason === 'document-hidden') {
      logger.info('Google Drive request paused because the app moved to the background', {
        label,
        method,
        url: safeUrl,
        elapsedMs: Date.now() - startedAt,
      });
      throw new GoogleDriveSyncCancelledError(
        'document-hidden',
        `Google Drive request ${label} paused because the app moved to the background`,
      );
    }
    logger.warn('Google Drive request failed', {
      label,
      method,
      url: safeUrl,
      elapsedMs: Date.now() - startedAt,
      timeoutMs,
      aborted,
      error: error instanceof Error ? error.message : String(error),
    });
    if (aborted) {
      throw new Error(`Drive request timed out during ${label} after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  }
};

const throwDriveResponseError = async (
  response: Response,
  operation: string,
  label: string,
): Promise<never> => {
  const bodyText = await readResponseText(response);
  const details = extractDriveErrorDetails(bodyText);
  logger.warn('Google Drive API response was not ok', {
    label,
    operation,
    status: response.status,
    statusText: response.statusText || null,
    code: details.code || null,
    reasons: details.reasons,
    message: details.message || null,
    bodyLength: details.bodyLength,
    needsReconnect: details.needsReconnect,
  });
  if (isDriveReauthorizationErrorDetails(response.status, details)) {
    clearGoogleDriveAccessTokenCache();
    throw new GoogleDriveReauthorizationRequiredError(
      `${operation} failed because Google Drive authorization must be refreshed`,
    );
  }
  const summary = formatDriveErrorSummary(details);
  throw new GoogleDriveResponseError(operation, label, response.status, summary);
};

export const getGoogleDriveStatus = async (): Promise<DriveSyncStatus> => {
  logger.debug('Google Drive status request started');
  const response = await apiFetch('/api/drive/status');
  if (!response.ok) {
    logger.warn('Google Drive status request failed', {
      status: response.status,
    });
    if (response.status === 401) {
      clearGoogleDriveAccessTokenCache();
      lastConnectedDriveEmail = null;
    }
    throw new Error(`Drive status failed: ${response.status}`);
  }
  const status = (await response.json()) as DriveSyncStatus;
  lastConnectedDriveEmail = status.connected ? normalizeEmail(status.email) || null : null;
  const accountKey = getDriveAccountKey(lastConnectedDriveEmail);
  if (!status.connected || (cachedAccessToken && (!accountKey || cachedAccessToken.accountKey !== accountKey))) {
    logger.debug('Google Drive access token cache cleared after status check', {
      connected: status.connected,
      accountKeyKnown: Boolean(accountKey),
      hadCachedToken: Boolean(cachedAccessToken),
      cacheAccountChanged: Boolean(cachedAccessToken && accountKey && cachedAccessToken.accountKey !== accountKey),
    });
    clearGoogleDriveAccessTokenCache();
  }
  logger.debug('Google Drive status request finished', {
    configured: status.configured,
    connected: status.connected,
    driveEmailPresent: Boolean(status.email),
    scopePresent: Boolean(status.scope),
    hasDriveAppDataScope: status.hasDriveAppDataScope ?? null,
    lastError: status.lastError || null,
  });
  return status;
};

const getDriveAccessToken = async (forceRefresh = false, context = 'drive-request'): Promise<string> => {
  const now = Date.now();
  const accountKey = getDriveAccountKey(lastConnectedDriveEmail);
  if (!accountKey) {
    logger.warn('Google Drive access token requested without account key', {
      context,
      currentUserPresent: Boolean(getCurrentUser()?.email),
      driveEmailKnown: Boolean(lastConnectedDriveEmail),
    });
    clearGoogleDriveAccessTokenCache();
    throw new Error('Drive token requested without a logged-in user');
  }
  if (
    !forceRefresh &&
    cachedAccessToken &&
    cachedAccessToken.accountKey === accountKey &&
    cachedAccessToken.expiresAt > now + TOKEN_REFRESH_SKEW_MS
  ) {
    if (now - lastTokenCacheHitLogAt >= DRIVE_TOKEN_CACHE_HIT_LOG_INTERVAL_MS) {
      lastTokenCacheHitLogAt = now;
      logger.debug('Google Drive access token cache hit', {
        context,
        expiresInMs: cachedAccessToken.expiresAt - now,
      });
    }
    return cachedAccessToken.token;
  }
  if (cachedAccessToken && cachedAccessToken.accountKey !== accountKey) {
    logger.debug('Google Drive access token cache cleared for account change', {
      context,
      hadCachedToken: true,
    });
    clearGoogleDriveAccessTokenCache();
  }

  logger.debug('Google Drive access token refresh requested', {
    context,
    forceRefresh,
    hadCachedToken: Boolean(cachedAccessToken),
  });
  const response = await apiFetch('/api/drive/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const errorText = await readResponseText(response);
    const details = extractDriveErrorDetails(errorText);
    logger.warn('Google Drive access token refresh failed', {
      context,
      status: response.status,
      code: details.code || null,
      reasons: details.reasons,
      message: details.message || null,
      needsReconnect: details.needsReconnect,
    });
    if (isDriveReauthorizationErrorDetails(response.status, details)) {
      clearGoogleDriveAccessTokenCache();
      throw new GoogleDriveReauthorizationRequiredError();
    }
    throw new Error(`Drive token failed: ${response.status}${details.message ? ` ${details.message}` : ''}`);
  }
  const payload = (await response.json()) as DriveAccessToken;
  if (payload.hasDriveAppDataScope === false) {
    logger.warn('Google Drive access token response is missing Drive app data scope', {
      context,
      scopePresent: Boolean(payload.scope),
    });
    clearGoogleDriveAccessTokenCache();
    throw new GoogleDriveReauthorizationRequiredError();
  }
  cachedAccessToken = {
    token: payload.accessToken,
    expiresAt: now + Math.max(60, payload.expiresIn || 3600) * 1000,
    accountKey,
  };
  logger.debug('Google Drive access token refreshed', {
    context,
    expiresIn: payload.expiresIn || 3600,
    scopePresent: Boolean(payload.scope),
    hasDriveAppDataScope: payload.hasDriveAppDataScope ?? null,
    tokenType: payload.tokenType || null,
  });
  return cachedAccessToken.token;
};

const driveFetch = async (
  url: string,
  init: RequestInit = {},
  retry = true,
  label = 'drive-request',
  timeoutMs = DRIVE_REQUEST_TIMEOUT_MS,
): Promise<Response> => {
  const token = await getDriveAccessToken(!retry, label);
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  const response = await fetchWithTimeout(
    url,
    {
      ...init,
      headers,
    },
    timeoutMs,
    label,
  );

  if (response.status === 401 && retry) {
    logger.warn('Google Drive request received 401; refreshing access token', {
      label,
      method: init.method || 'GET',
      url: redactDriveUrlForLog(url),
    });
    clearGoogleDriveAccessTokenCache();
    return driveFetch(url, init, false, label, timeoutMs);
  }

  return response;
};

export const startGoogleDriveConnect = async (): Promise<void> => {
  const platform = getAppPlatform();
  logger.info('Google Drive connect start requested', {
    platform,
    returnUrl: window.location.href,
  });
  const response = await apiFetch('/api/drive/connect/start', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      platform,
      returnUrl: window.location.href,
    }),
  });
  if (!response.ok) {
    logger.warn('Google Drive connect start request failed', {
      platform,
      status: response.status,
    });
    throw new Error(`Drive connect start failed: ${response.status} ${await readResponseText(response)}`);
  }

  const payload = await response.json();
  if (!payload?.authUrl) {
    logger.error('Google Drive connect start did not return an auth URL', {
      platform,
      payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : [],
    });
    throw new Error('Drive connect start did not return authUrl');
  }
  logger.info('Opening Google Drive OAuth flow', {
    platform,
    authHost: (() => {
      try {
        return new URL(payload.authUrl).host;
      } catch {
        return '(invalid)';
      }
    })(),
  });
  await openExternalUrl(payload.authUrl);
};

export const disconnectGoogleDrive = async (): Promise<void> => {
  const response = await apiFetch('/api/drive/disconnect', {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Drive disconnect failed: ${response.status}`);
  }
  clearGoogleDriveAccessTokenCache();
  lastConnectedDriveEmail = null;
};

const escapeDriveQueryString = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const findAppDataFileByName = async (name: string): Promise<DriveListedFile | null> => {
  logger.debug('Google Drive file lookup started', { name });
  const params = new URLSearchParams({
    spaces: 'appDataFolder',
    q: `name='${escapeDriveQueryString(name)}' and trashed=false`,
    fields: 'files(id,name,mimeType,size,modifiedTime)',
    pageSize: '10',
  });
  const response = await driveFetch(`${DRIVE_API_BASE}/files?${params.toString()}`, {}, true, 'drive-file-lookup');
  if (!response.ok) {
    await throwDriveResponseError(response, 'Drive file lookup', 'drive-file-lookup');
  }
  const payload = await response.json();
  const files = (payload.files || []) as DriveListedFile[];
  logger.debug('Google Drive file lookup finished', {
    name,
    fileCount: files.length,
    firstFileIdPresent: Boolean(files[0]?.id),
  });
  return files[0] || null;
};

const getDriveFileMetadata = async (fileId: string): Promise<DriveListedFile> => {
  logger.debug('Google Drive file metadata lookup started', {
    fileIdPresent: Boolean(fileId),
  });
  const params = new URLSearchParams({
    fields: 'id,name,mimeType,size,modifiedTime,appProperties',
  });
  const response = await driveFetch(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?${params.toString()}`,
    {},
    true,
    'drive-file-metadata',
  );
  if (!response.ok) {
    await throwDriveResponseError(response, 'Drive file metadata lookup', 'drive-file-metadata');
  }
  const metadata = (await response.json()) as DriveListedFile;
  logger.debug('Google Drive file metadata lookup finished', {
    fileIdPresent: Boolean(metadata.id),
    namePresent: Boolean(metadata.name),
    size: metadata.size || null,
    modifiedTime: metadata.modifiedTime || null,
    appPropertiesPresent: Boolean(metadata.appProperties),
  });
  return metadata;
};

const downloadDriveJson = async <T,>(fileId: string): Promise<T> => {
  logger.debug('Google Drive JSON download started', {
    fileIdPresent: Boolean(fileId),
  });
  const response = await driveFetch(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?alt=media`,
    {},
    true,
    'drive-json-download',
  );
  if (!response.ok) {
    await throwDriveResponseError(response, 'Drive JSON download', 'drive-json-download');
  }
  const payload = await response.json();
  logger.debug('Google Drive JSON download parsed', {
    fileIdPresent: Boolean(fileId),
  });
  return payload;
};

const downloadDriveBlob = async (fileId: string): Promise<Blob> => {
  logger.debug('Google Drive blob download started', {
    fileIdPresent: Boolean(fileId),
  });
  const response = await driveFetch(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?alt=media`,
    {},
    true,
    'drive-blob-download',
    DRIVE_FILE_TRANSFER_TIMEOUT_MS,
  );
  if (!response.ok) {
    await throwDriveResponseError(response, 'Drive file download', 'drive-blob-download');
  }
  const blob = await response.blob();
  logger.debug('Google Drive blob download finished', {
    fileIdPresent: Boolean(fileId),
    size: blob.size,
    type: blob.type || null,
  });
  return blob;
};

const createUploadSession = async (
  name: string,
  mimeType: string,
  existingFileId?: string,
  appProperties?: Record<string, string>,
): Promise<string> => {
  const metadata: Record<string, unknown> = {
    name,
    mimeType,
    appProperties,
  };
  if (!existingFileId) {
    metadata.parents = ['appDataFolder'];
  }

  const url = existingFileId
    ? `${DRIVE_UPLOAD_BASE}/files/${encodeURIComponent(existingFileId)}?uploadType=resumable&fields=id,name,mimeType,size,modifiedTime`
    : `${DRIVE_UPLOAD_BASE}/files?uploadType=resumable&fields=id,name,mimeType,size,modifiedTime`;

  logger.debug('Google Drive upload session creation started', {
    name,
    mimeType,
    existingFileIdPresent: Boolean(existingFileId),
    kind: appProperties?.kind || null,
  });
  const response = await driveFetch(
    url,
    {
      method: existingFileId ? 'PATCH' : 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Upload-Content-Type': mimeType,
      },
      body: JSON.stringify(metadata),
    },
    true,
    'drive-upload-session',
  );

  if (!response.ok) {
    await throwDriveResponseError(response, 'Drive upload session', 'drive-upload-session');
  }

  const uploadUrl = response.headers.get('Location');
  if (!uploadUrl) {
    throw new Error('Drive upload session did not include Location header');
  }
  logger.debug('Google Drive upload session created', {
    name,
    mimeType,
    existingFileIdPresent: Boolean(existingFileId),
    kind: appProperties?.kind || null,
    uploadUrlPresent: true,
  });
  return uploadUrl;
};

const uploadBlobToDrive = async (
  blob: Blob,
  name: string,
  mimeType: string,
  existingFileId: string | undefined,
  appProperties: Record<string, string>,
): Promise<DriveUploadResult> => {
  logger.debug('Google Drive blob upload started', {
    name,
    mimeType,
    size: blob.size,
    kind: appProperties.kind || null,
    bookIdPresent: Boolean(appProperties.bookId),
    existingFileIdPresent: Boolean(existingFileId),
  });
  const putUpload = async (uploadUrl: string, forceRefresh = false) => {
    const token = await getDriveAccessToken(forceRefresh, 'drive-upload-put');
    return fetchWithTimeout(
      uploadUrl,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': mimeType,
        },
        body: blob,
      },
      DRIVE_FILE_TRANSFER_TIMEOUT_MS,
      'drive-upload-put',
    );
  };

  let uploadUrl = await createUploadSession(name, mimeType, existingFileId, appProperties);
  let response = await putUpload(uploadUrl);

  if (response.status === 401) {
    logger.warn('Google Drive upload PUT received 401; refreshing access token', {
      name,
      kind: appProperties.kind || null,
      existingFileIdPresent: Boolean(existingFileId),
    });
    clearGoogleDriveAccessTokenCache();
    response = await putUpload(uploadUrl, true);
  }

  if (response.status === 404 && existingFileId) {
    logger.warn('Google Drive upload session referenced missing file; recreating upload', {
      name,
      kind: appProperties.kind || null,
      existingFileIdPresent: true,
    });
    uploadUrl = await createUploadSession(name, mimeType, undefined, appProperties);
    response = await putUpload(uploadUrl);
  }

  if (!response.ok) {
    await throwDriveResponseError(response, 'Drive upload', 'drive-upload-put');
  }
  const uploaded = (await response.json()) as DriveUploadResult;
  logger.debug('Google Drive blob upload finished', {
    name,
    kind: appProperties.kind || null,
    status: response.status,
    uploadedFileIdPresent: Boolean(uploaded.id),
    uploadedName: uploaded.name || null,
    uploadedSize: uploaded.size || null,
  });
  return uploaded;
};

const deleteDriveFile = async (fileId?: string): Promise<void> => {
  if (!fileId) {
    logger.debug('Google Drive delete skipped; no file id');
    return;
  }
  logger.debug('Google Drive delete started', {
    fileIdPresent: true,
  });
  const response = await driveFetch(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}`,
    {
      method: 'DELETE',
    },
    true,
    'drive-delete',
  );
  if (!response.ok && response.status !== 404) {
    await throwDriveResponseError(response, 'Drive delete', 'drive-delete');
  }
  logger.debug('Google Drive delete finished', {
    status: response.status,
    ok: response.ok || response.status === 404,
  });
};

const loadRemoteManifest = async (): Promise<{ manifest: DriveSyncManifestV1 | null; file: DriveListedFile | null }> => {
  logger.debug('Google Drive sync: looking up remote manifest');
  const file = await findAppDataFileByName(MANIFEST_NAME);
  if (!file) {
    logger.debug('Google Drive sync: no remote manifest found');
    return { manifest: null, file: null };
  }
  const manifest = await downloadDriveJson<DriveSyncManifestV1>(file.id);
  if (manifest?.version !== 1) {
    logger.warn('Ignoring unsupported Drive sync manifest version', {
      version: manifest?.version,
    });
    return { manifest: null, file };
  }
  logger.debug('Google Drive sync: loaded remote manifest', {
    generatedAt: manifest.generatedAt,
    bookCount: Object.keys(manifest.books || {}).length,
    deletedBookCount: Object.keys(manifest.deletedBooks || {}).length,
    deletedGroupCount: Object.keys(manifest.deletedGroups || {}).length,
  });
  return { manifest, file };
};

const toManifestBook = async (book: BookMeta): Promise<DriveManifestBook> => {
  const drive = book.cloud?.googleDrive;
  const entry: DriveManifestBook = {
    meta: createDriveManifestBookMeta(book),
  };
  if (drive?.fileId) {
    entry.epub = {
      fileId: drive.fileId,
      kind: 'epub',
      name: drive.fileName || getBlobName(book, 'epub'),
      mimeType: drive.mimeType || 'application/epub+zip',
      size: drive.size,
      hash: drive.hash || book.hash,
      uploadedAt: drive.uploadedAt || Date.now(),
    };
  }
  if (drive?.coverFileId) {
    entry.cover = {
      fileId: drive.coverFileId,
      kind: 'cover',
      name: getBlobName(book, 'cover'),
      mimeType: 'image/jpeg',
      size: drive.coverSize,
      hash: drive.coverHash,
      uploadedAt: drive.uploadedAt || Date.now(),
    };
  }
  if (drive?.notesFileId && drive.notesHash) {
    entry.notes = {
      fileId: drive.notesFileId,
      kind: 'notes',
      name: getBlobName(book, 'notes'),
      mimeType: 'application/json',
      size: drive.notesSize,
      hash: drive.notesHash,
      uploadedAt: drive.notesUploadedAt || drive.uploadedAt || Date.now(),
    };
  }
  return entry;
};

const buildLocalManifest = async (state: LocalDriveSyncState): Promise<DriveSyncManifestV1> => {
  const books = getBooks();
  const manifestBooks: Record<string, DriveManifestBook> = {};
  for (const book of Object.values(books)) {
    if (!book?.id || book.location === 'trash') {
      continue;
    }
    manifestBooks[book.id] = await toManifestBook(book);
  }

  const currentUser = getCurrentUser();
  const manifestPrefs = currentUser?.prefs
    ? ({ ...currentUser.prefs } as Prefs & Record<string, unknown>)
    : undefined;
  if (manifestPrefs) {
    delete manifestPrefs._id;
    delete manifestPrefs._rev;
  }
  const manifest: DriveSyncManifestV1 = {
    version: 1,
    generatedAt: Date.now(),
    deviceId: state.deviceId,
    userEmail: currentUser?.email,
    prefs: manifestPrefs,
    following: getFollowing() || undefined,
    misc: getMisc() as Record<string, unknown> | null,
    notesStatus: getNotesStatus(),
    books: manifestBooks,
    deletedBooks: state.deletedBooks || {},
  };
  const deletedGroups = state.deletedGroups || {};
  if (Object.keys(deletedGroups).length > 0) {
    manifest.deletedGroups = deletedGroups;
  }
  logger.debug('Google Drive sync: built local manifest', {
    bookCount: Object.keys(manifestBooks).length,
    deletedBookCount: Object.keys(manifest.deletedBooks).length,
    deletedGroupCount: Object.keys(manifest.deletedGroups || {}).length,
    hasPrefs: Boolean(manifest.prefs),
  });
  return manifest;
};

const markBookCloudState = (
  books: BookCollection,
  bookId: string,
  update: Partial<GoogleDriveBookState>,
): void => {
  const book = books[bookId];
  if (!book) {
    return;
  }
  books[bookId] = {
    ...book,
    cloud: {
      ...book.cloud,
      googleDrive: {
        provider: 'google-drive',
        ...book.cloud?.googleDrive,
        ...update,
      },
    },
  };
};

const uploadBookFiles = async (
  manifest: DriveSyncManifestV1,
  reason: string,
  syncStartedAt: number,
): Promise<DriveSyncManifestV1> => {
  const books = { ...getBooks() };
  let changedBooks = false;
  let booksChangedSincePersist = false;
  let filesSincePersist = 0;
  let processedEntries = 0;
  let skippedMissingBook = 0;
  let skippedDeletedEntry = 0;
  let missingLocalEpub = 0;
  let skippedCloudOnlyEpub = 0;
  let unchangedEpub = 0;
  let epubUploads = 0;
  let coverUploads = 0;
  let unchangedCovers = 0;
  let notesUploads = 0;
  let unchangedNotes = 0;
  let removedEmptyNotesRefs = 0;
  let uploadedFiles = 0;
  let uploadedBytes = 0;
  let nextEntryIndex = 0;
  let lastCloudStatePersistAt = Date.now();
  let lastProgressLogAt = 0;
  const manifestEntries = Object.entries(manifest.books);
  const totalEntries = manifestEntries.length;

  const emitProgress = (
    forceLog = false,
    extra: Partial<DriveSyncProgress> = {},
  ) => {
    const progress: DriveSyncProgress = {
      reason,
      phase: 'upload-files',
      current: processedEntries,
      total: totalEntries,
      uploadedFiles,
      skippedFiles:
        unchangedEpub +
        unchangedCovers +
        unchangedNotes +
        missingLocalEpub +
        skippedCloudOnlyEpub +
        skippedMissingBook +
        skippedDeletedEntry,
      uploadedBytes,
      elapsedMs: Date.now() - syncStartedAt,
      ...extra,
    };
    dispatchDriveSyncProgress(progress);
    const now = Date.now();
    if (forceLog || now - lastProgressLogAt >= DRIVE_UPLOAD_PROGRESS_LOG_INTERVAL_MS) {
      lastProgressLogAt = now;
      logger.debug('Google Drive sync: upload progress', progress);
    }
  };

  const persistCloudState = (force = false) => {
    if (!booksChangedSincePersist) {
      return;
    }
    const now = Date.now();
    if (
      !force &&
      filesSincePersist < DRIVE_CLOUD_STATE_PERSIST_EVERY_FILES &&
      now - lastCloudStatePersistAt < DRIVE_UPLOAD_PROGRESS_LOG_INTERVAL_MS
    ) {
      return;
    }
    updateBooksCache(books, 'google-drive-sync-cloud-state');
    logger.debug('Google Drive sync: persisted cloud file refs during upload', {
      reason,
      uploadedFiles,
      filesSincePersist,
      processedEntries,
      totalEntries,
      elapsedMs: now - syncStartedAt,
    });
    booksChangedSincePersist = false;
    filesSincePersist = 0;
    lastCloudStatePersistAt = now;
  };

  const markBooksChanged = () => {
    changedBooks = true;
    booksChangedSincePersist = true;
  };

  const recordUploadedFile = (size: number) => {
    uploadedFiles += 1;
    uploadedBytes += size;
    filesSincePersist += 1;
    persistCloudState(false);
  };

  logger.debug('Google Drive sync: checking local book files for upload', {
    manifestBookCount: totalEntries,
    concurrency: DRIVE_FILE_UPLOAD_CONCURRENCY,
  });
  emitProgress(true);

  const processEntry = async ([bookId, entry]: [string, DriveManifestBook], entryIndex: number) => {
    const book = books[bookId];
    if (!book) {
      skippedMissingBook += 1;
      return;
    }
    if (entry.deletedAt) {
      skippedDeletedEntry += 1;
      return;
    }

    const localDriveState = book.cloud?.googleDrive;
    const localDriveStateSaysCloudOnly = localDriveState?.localFileAvailable === false;
    let verifiedLocalEpubHash: string | undefined;
    let file: Blob | undefined;
    if (localDriveStateSaysCloudOnly) {
      const possibleLocalFile = await getFile(bookId);
      if (possibleLocalFile && book.hash) {
        const possibleLocalFileHash = (await calculateFileHash(possibleLocalFile)) as string;
        if (possibleLocalFileHash === book.hash) {
          file = possibleLocalFile;
          verifiedLocalEpubHash = possibleLocalFileHash;
        } else {
          logger.debug('Google Drive sync skipped stale cloud-only local EPUB file', {
            bookId,
            title: book.title,
            expectedHashPresent: true,
            localFileHashPresent: true,
          });
        }
      }
    } else {
      file = await getFile(bookId);
    }
    if (file) {
      const hash = (verifiedLocalEpubHash || book.hash || (await calculateFileHash(file))) as string;
      const existingRef = entry.epub;
      if (!existingRef?.fileId || existingRef.hash !== hash) {
        const name = getBlobName(book, 'epub');
        logger.info('Uploading EPUB to Google Drive app data', {
          bookId,
          title: book.title,
          size: file.size,
          entryIndex: entryIndex + 1,
          totalEntries,
        });
        emitProgress(false, {
          current: entryIndex + 1,
          currentBookId: bookId,
          currentBookTitle: book.title,
          currentFileKind: 'epub',
          currentFileSize: file.size,
        });
        const uploaded = await uploadBlobToDrive(
          file,
          name,
          file.type || 'application/epub+zip',
          existingRef?.fileId,
          {
            kind: 'epub',
            bookId,
            hash,
          },
        );
        entry.epub = {
          fileId: uploaded.id,
          kind: 'epub',
          name: uploaded.name || name,
          mimeType: uploaded.mimeType || file.type || 'application/epub+zip',
          size: toNumber(uploaded.size) || file.size,
          hash,
          uploadedAt: Date.now(),
          modifiedTime: uploaded.modifiedTime,
        };
        markBookCloudState(books, bookId, {
          fileId: entry.epub.fileId,
          hash,
          size: entry.epub.size,
          fileName: entry.epub.name,
          mimeType: entry.epub.mimeType,
          uploadedAt: entry.epub.uploadedAt,
          downloadedAt: Date.now(),
          localFileAvailable: true,
        });
        epubUploads += 1;
        markBooksChanged();
        recordUploadedFile(file.size);
        emitProgress(false, {
          current: entryIndex + 1,
          currentBookId: bookId,
          currentBookTitle: book.title,
          currentFileKind: 'epub',
          currentFileSize: file.size,
        });
      } else if (book.cloud?.googleDrive?.localFileAvailable !== true) {
        markBookCloudState(books, bookId, {
          localFileAvailable: true,
          downloadedAt: book.cloud?.googleDrive?.downloadedAt || Date.now(),
        });
        markBooksChanged();
      } else {
        unchangedEpub += 1;
      }
    } else if (localDriveStateSaysCloudOnly) {
      skippedCloudOnlyEpub += 1;
    } else {
      missingLocalEpub += 1;
    }

    const cover = await getFile(`cover-${bookId}`);
    const coverHash = cover ? (await calculateFileHash(cover)) as string : '';
    if (cover && book.coverImageHash && book.coverImageHash !== coverHash) {
      logger.info('Google Drive sync skipped a thumbnail awaiting its newer image', {
        bookId,
        coverUpdatedAt: book.coverUpdatedAt,
        expectedHash: book.coverImageHash,
        localHash: coverHash,
      });
    } else if (cover) {
      const existingCoverRef = entry.cover;
      if (!existingCoverRef?.fileId || existingCoverRef.hash !== coverHash) {
        const name = getBlobName(book, 'cover');
        emitProgress(false, {
          current: entryIndex + 1,
          currentBookId: bookId,
          currentBookTitle: book.title,
          currentFileKind: 'cover',
          currentFileSize: cover.size,
        });
        const uploaded = await uploadBlobToDrive(
          cover,
          name,
          cover.type || 'image/jpeg',
          existingCoverRef?.fileId,
          {
            kind: 'cover',
            bookId,
            hash: coverHash,
            ...(book.coverUpdatedAt && { coverUpdatedAt: String(book.coverUpdatedAt) }),
          },
        );
        entry.cover = {
          fileId: uploaded.id,
          kind: 'cover',
          name: uploaded.name || name,
          mimeType: uploaded.mimeType || cover.type || 'image/jpeg',
          size: toNumber(uploaded.size) || cover.size,
          hash: coverHash,
          uploadedAt: Date.now(),
          modifiedTime: uploaded.modifiedTime,
        };
        markBookCloudState(books, bookId, {
          coverFileId: entry.cover.fileId,
          coverHash,
          coverSize: entry.cover.size,
        });
        coverUploads += 1;
        markBooksChanged();
        recordUploadedFile(cover.size);
        emitProgress(false, {
          current: entryIndex + 1,
          currentBookId: bookId,
          currentBookTitle: book.title,
          currentFileKind: 'cover',
          currentFileSize: cover.size,
        });
      } else {
        unchangedCovers += 1;
      }
    }

    if (hasBookNotes(book.notes)) {
      const notesBlob = createNotesBlob(book.notes || {});
      const notesHash = (await calculateFileHash(notesBlob)) as string;
      const existingNotesRef = entry.notes;
      if (!existingNotesRef?.fileId || existingNotesRef.hash !== notesHash) {
        const name = getBlobName(book, 'notes');
        emitProgress(false, {
          current: entryIndex + 1,
          currentBookId: bookId,
          currentBookTitle: book.title,
          currentFileKind: 'notes',
          currentFileSize: notesBlob.size,
        });
        const uploaded = await uploadBlobToDrive(
          notesBlob,
          name,
          'application/json',
          existingNotesRef?.fileId,
          {
            kind: 'notes',
            bookId,
            hash: notesHash,
          },
        );
        entry.notes = {
          fileId: uploaded.id,
          kind: 'notes',
          name: uploaded.name || name,
          mimeType: uploaded.mimeType || 'application/json',
          size: toNumber(uploaded.size) || notesBlob.size,
          hash: notesHash,
          uploadedAt: Date.now(),
          modifiedTime: uploaded.modifiedTime,
        };
        markBookCloudState(books, bookId, {
          notesFileId: entry.notes.fileId,
          notesHash,
          notesSize: entry.notes.size,
          notesUploadedAt: entry.notes.uploadedAt,
        });
        notesUploads += 1;
        markBooksChanged();
        recordUploadedFile(notesBlob.size);
        emitProgress(false, {
          current: entryIndex + 1,
          currentBookId: bookId,
          currentBookTitle: book.title,
          currentFileKind: 'notes',
          currentFileSize: notesBlob.size,
        });
      } else {
        unchangedNotes += 1;
      }
    } else if (entry.notes?.fileId || localDriveState?.notesFileId) {
      const notesFileIdsToDelete = Array.from(
        new Set([entry.notes?.fileId, localDriveState?.notesFileId].filter(Boolean) as string[]),
      );
      for (const notesFileId of notesFileIdsToDelete) {
        logger.info('Deleting obsolete Google Drive notes file for book with no local notes', {
          bookId,
          title: book.title,
          notesFileIdPresent: true,
        });
        await deleteDriveFile(notesFileId);
      }
      delete entry.notes;
      markBookCloudState(books, bookId, {
        notesFileId: undefined,
        notesHash: undefined,
        notesSize: undefined,
        notesUploadedAt: undefined,
      });
      removedEmptyNotesRefs += 1;
      markBooksChanged();
    }
  };

  let firstError: unknown = null;
  const workerCount = Math.min(DRIVE_FILE_UPLOAD_CONCURRENCY, Math.max(1, totalEntries));
  const workers = Array.from({ length: workerCount }, async () => {
    while (!firstError) {
      const entryIndex = nextEntryIndex;
      nextEntryIndex += 1;
      if (entryIndex >= manifestEntries.length) {
        return;
      }
      try {
        await processEntry(manifestEntries[entryIndex], entryIndex);
      } catch (error) {
        firstError = error;
        throw error;
      } finally {
        processedEntries += 1;
        persistCloudState(false);
        emitProgress(false);
      }
    }
  });

  const results = await Promise.allSettled(workers);
  const failedWorker = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  if (failedWorker) {
    persistCloudState(true);
    logger.warn('Google Drive sync: upload worker failed', {
      reason,
      processedEntries,
      totalEntries,
      uploadedFiles,
      uploadedBytes,
      error: failedWorker.reason instanceof Error ? failedWorker.reason.message : String(failedWorker.reason),
    });
    throw failedWorker.reason;
  }

  persistCloudState(true);

  if (changedBooks) {
    for (const [bookId, entry] of Object.entries(manifest.books)) {
      const book = books[bookId];
      if (book) {
        entry.meta = createDriveManifestBookMeta(book);
      }
    }
  }

  logger.debug('Google Drive sync: finished local file upload pass', {
    changedBooks,
    processedEntries,
    totalEntries,
    skippedMissingBook,
    skippedDeletedEntry,
    missingLocalEpub,
    skippedCloudOnlyEpub,
    unchangedEpub,
    unchangedCovers,
    unchangedNotes,
    epubUploads,
    coverUploads,
    notesUploads,
    removedEmptyNotesRefs,
    uploadedFiles,
    uploadedBytes,
  });
  emitProgress(true, { current: totalEntries });
  return manifest;
};

type RemoteCoverDownloadSummary = {
  remoteCoversWithFiles: number;
  localCoversAlreadyAvailable: number;
  staleLocalCovers: number;
  downloadedRemoteCovers: number;
  failedRemoteCoverDownloads: number;
  downloadedCoverBytes: number;
  changedBooks: boolean;
};

type RemoteNotesDownloadResult = {
  notes?: NotesGroup;
  downloaded: boolean;
  verifiedRef: boolean;
  localDeletion: boolean;
  sidecarMissing: boolean;
  notesHash?: string;
  repairedRef?: DriveFileRef;
};

const isLocallyEmptiedSyncedNotes = (
  localBook: BookMeta | undefined,
  notesRef: DriveFileRef | undefined,
): boolean => {
  if (!localBook || hasBookNotes(localBook.notes) || !notesRef?.fileId) {
    return false;
  }
  const localDrive = localBook.cloud?.googleDrive;
  if (!localDrive?.notesFileId && !localDrive?.notesHash) {
    return false;
  }
  if (notesRef.hash && localDrive.notesHash && notesRef.hash !== localDrive.notesHash) {
    return false;
  }
  return localDrive.notesFileId === notesRef.fileId || Boolean(notesRef.hash && localDrive.notesHash === notesRef.hash);
};

const downloadRemoteBookNotes = async (
  bookId: string,
  remoteEntry: DriveManifestBook,
  localBook: BookMeta | undefined,
): Promise<RemoteNotesDownloadResult> => {
  const notesRef = remoteEntry.notes;
  const inlineNotes = remoteEntry.meta?.notes;
  if (!notesRef?.fileId) {
    return {
      notes: inlineNotes,
      downloaded: false,
      verifiedRef: false,
      localDeletion: false,
      sidecarMissing: false,
    };
  }

  if (isLocallyEmptiedSyncedNotes(localBook, notesRef)) {
    logger.info('Google Drive sync kept locally emptied notes over stale remote notes file', {
      bookId,
      title: remoteEntry.meta?.title || null,
      notesFileIdPresent: true,
      notesHashMatches: Boolean(notesRef.hash && localBook?.cloud?.googleDrive?.notesHash === notesRef.hash),
    });
    return {
      notes: localBook?.notes || {},
      downloaded: false,
      verifiedRef: false,
      localDeletion: true,
      sidecarMissing: false,
      notesHash: localBook?.cloud?.googleDrive?.notesHash || notesRef.hash,
    };
  }

  if (notesRef.hash && localBook?.cloud?.googleDrive?.notesHash === notesRef.hash) {
    const localNotes = localBook.notes || {};
    const localNotesHash = (await calculateFileHash(createNotesBlob(localNotes))) as string;
    if (localNotesHash === notesRef.hash) {
      return {
        notes: localNotes,
        downloaded: false,
        verifiedRef: true,
        localDeletion: false,
        sidecarMissing: false,
        notesHash: localNotesHash,
      };
    }
    logger.warn('Google Drive notes hash cache did not match local note payload; downloading remote notes', {
      bookId,
      title: remoteEntry.meta?.title || null,
      notesFileIdPresent: true,
      expectedHashPresent: true,
      localNotesHashPresent: Boolean(localNotesHash),
    });
  }

  try {
    logger.info('Downloading Google Drive notes for synced book', {
      bookId,
      title: remoteEntry.meta?.title || null,
      notesFileIdPresent: true,
      expectedHashPresent: Boolean(notesRef.hash),
      expectedSize: notesRef.size || null,
    });
    const notes = await downloadDriveJson<NotesGroup>(notesRef.fileId);
    const notesHash = (await calculateFileHash(createNotesBlob(notes || {}))) as string;
    let repairedRef: DriveFileRef | undefined;
    if (notesRef.hash && notesHash !== notesRef.hash) {
      logger.warn('Google Drive notes hash did not match manifest; checking sidecar metadata for an interrupted sync', {
        bookId,
        title: remoteEntry.meta?.title || null,
        notesFileIdPresent: true,
        expectedHashPresent: true,
        downloadedHashPresent: Boolean(notesHash),
        manifestSize: notesRef.size || null,
      });
      const metadata = await getDriveFileMetadata(notesRef.fileId);
      const metadataHash = metadata.appProperties?.hash;
      const metadataKind = metadata.appProperties?.kind;
      const metadataBookId = metadata.appProperties?.bookId;
      const metadataVerifiesDownloadedNotes =
        metadataHash === notesHash && metadataKind === 'notes' && metadataBookId === bookId;
      logger.warn('Google Drive notes sidecar metadata verification finished', {
        bookId,
        title: remoteEntry.meta?.title || null,
        metadataHashMatches: metadataHash === notesHash,
        metadataKindMatches: metadataKind === 'notes',
        metadataBookMatches: metadataBookId === bookId,
        metadataSize: metadata.size || null,
        metadataModifiedTime: metadata.modifiedTime || null,
      });
      if (!metadataVerifiesDownloadedNotes) {
        throw new Error('Downloaded Google Drive notes hash did not match manifest hash');
      }

      const metadataModifiedAt = metadata.modifiedTime ? Date.parse(metadata.modifiedTime) : Number.NaN;
      repairedRef = {
        ...notesRef,
        name: metadata.name || notesRef.name,
        mimeType: metadata.mimeType || notesRef.mimeType,
        size: Number(metadata.size) || createNotesBlob(notes || {}).size,
        hash: notesHash,
        uploadedAt: Number.isFinite(metadataModifiedAt) ? metadataModifiedAt : notesRef.uploadedAt,
        modifiedTime: metadata.modifiedTime || notesRef.modifiedTime,
      };
      logger.warn('Recovered Google Drive notes sidecar written after its stale manifest entry', {
        bookId,
        title: remoteEntry.meta?.title || null,
        repairedHashPresent: true,
        repairedSize: repairedRef.size || null,
        repairedModifiedTime: repairedRef.modifiedTime || null,
      });
    }
    return {
      notes: notes || {},
      downloaded: true,
      verifiedRef: true,
      localDeletion: false,
      sidecarMissing: false,
      notesHash,
      repairedRef,
    };
  } catch (error) {
    if (isGoogleDriveReauthorizationRequiredError(error)) {
      throw error;
    }
    const missingNotesSidecar = isGoogleDriveResponseErrorStatus(error, 404, 'Drive JSON download');
    if (missingNotesSidecar && localBook && !hasBookNotes(localBook.notes)) {
      logger.warn('Google Drive notes file is missing; keeping local empty notes so manifest can be repaired', {
        bookId,
        title: remoteEntry.meta?.title || null,
        notesFileIdPresent: true,
        localNotesEmpty: true,
      });
      return {
        notes: localBook.notes || {},
        downloaded: false,
        verifiedRef: false,
        localDeletion: true,
        sidecarMissing: true,
        notesHash: localBook.cloud?.googleDrive?.notesHash || notesRef.hash,
      };
    }
    if (missingNotesSidecar && inlineNotes !== undefined) {
      const inlineNotesHash = (await calculateFileHash(createNotesBlob(inlineNotes || {}))) as string;
      if (notesRef.hash && inlineNotesHash !== notesRef.hash) {
        logger.warn('Missing Google Drive notes file had mismatched inline fallback notes hash', {
          bookId,
          title: remoteEntry.meta?.title || null,
          notesFileIdPresent: true,
          expectedHashPresent: true,
          inlineNotesHashPresent: Boolean(inlineNotesHash),
        });
        throw new Error('Missing Google Drive notes file and inline fallback notes hash did not match manifest hash');
      }
      logger.warn('Google Drive notes file is missing; using inline notes fallback so manifest can be repaired', {
        bookId,
        title: remoteEntry.meta?.title || null,
        notesFileIdPresent: true,
        inlineNotesHashPresent: Boolean(inlineNotesHash),
      });
      return {
        notes: inlineNotes || {},
        downloaded: false,
        verifiedRef: false,
        localDeletion: false,
        sidecarMissing: true,
        notesHash: inlineNotesHash,
      };
    }
    logger.warn('Failed to download Google Drive notes for synced book', {
      bookId,
      title: remoteEntry.meta?.title || null,
      notesFileIdPresent: true,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

const recoverRemoteCoverRef = async (
  bookId: string,
  remoteEntry: DriveManifestBook,
  expectedHash: string,
): Promise<{ cover: DriveFileRef; coverUpdatedAt: number }> => {
  const cover = remoteEntry.cover!;
  const metadata = await getDriveFileMetadata(cover.fileId);
  const metadataHashMatches = metadata.appProperties?.hash === expectedHash;
  const metadataKindMatches = metadata.appProperties?.kind === 'cover';
  const metadataBookMatches = metadata.appProperties?.bookId === bookId;
  logger.warn('Google Drive cover sidecar metadata verification finished', {
    bookId,
    manifestHash: cover.hash || null,
    expectedHash,
    metadataHashMatches,
    metadataKindMatches,
    metadataBookMatches,
    metadataModifiedTime: metadata.modifiedTime || null,
  });
  if (!metadataHashMatches || !metadataKindMatches || !metadataBookMatches) {
    throw new Error('Google Drive cover sidecar metadata did not verify the image hash');
  }
  const modifiedAt = metadata.modifiedTime ? Date.parse(metadata.modifiedTime) : Number.NaN;
  const uploadedAt = Number.isFinite(modifiedAt) ? modifiedAt : cover.uploadedAt;
  return {
    cover: {
      ...cover,
      name: metadata.name || cover.name,
      mimeType: metadata.mimeType || cover.mimeType,
      size: toNumber(metadata.size) || cover.size,
      hash: expectedHash,
      uploadedAt,
      modifiedTime: metadata.modifiedTime || cover.modifiedTime,
    },
    coverUpdatedAt:
      toNumber(metadata.appProperties?.coverUpdatedAt) ||
      (remoteEntry.meta.coverImageHash === expectedHash ? remoteEntry.meta.coverUpdatedAt : 0) ||
      uploadedAt,
  };
};

const downloadRemoteCovers = async (
  remoteManifest: DriveSyncManifestV1,
  books: BookCollection,
  reason: string,
  syncStartedAt: number,
): Promise<RemoteCoverDownloadSummary> => {
  const coverEntries = Object.entries(remoteManifest.books || {}).filter(([bookId, remoteEntry]) => {
    const remoteDeletedAt = remoteEntry.deletedAt || remoteManifest.deletedBooks?.[bookId]?.deletedAt;
    return !remoteDeletedAt && Boolean(remoteEntry.cover?.fileId);
  });
  let nextCoverIndex = 0;
  let processedCovers = 0;
  let localCoversAlreadyAvailable = 0;
  let staleLocalCovers = 0;
  let downloadedRemoteCovers = 0;
  let failedRemoteCoverDownloads = 0;
  let downloadedCoverBytes = 0;
  let changedBooks = false;

  const emitCoverProgress = (forceLog = false) => {
    const progress: DriveSyncProgress = {
      reason,
      phase: 'download-covers',
      current: processedCovers,
      total: coverEntries.length,
      uploadedBytes: downloadedCoverBytes,
      elapsedMs: Date.now() - syncStartedAt,
    };
    dispatchDriveSyncProgress(progress);
    if (forceLog) {
      logger.debug('Google Drive sync: cover download progress', progress);
    }
  };

  const processCover = async ([bookId, remoteEntry]: [string, DriveManifestBook]) => {
    let cover = remoteEntry.cover;
    if (!cover?.fileId) {
      return;
    }
    try {
      let recoveredCoverUpdatedAt: number | undefined;
      if (remoteEntry.meta?.coverImageHash && remoteEntry.meta.coverImageHash !== cover.hash) {
        logger.warn('Google Drive cover edit differs from manifest file ref; checking for an interrupted upload', {
          bookId,
          expectedHash: remoteEntry.meta.coverImageHash,
          manifestHash: cover.hash || null,
        });
        const recovered = await recoverRemoteCoverRef(bookId, remoteEntry, remoteEntry.meta.coverImageHash);
        cover = recovered.cover;
        recoveredCoverUpdatedAt = recovered.coverUpdatedAt;
      }
      const localBook = books[bookId];
      const coverKey = `cover-${bookId}`;
      const existingCover = await getFile(coverKey);
      let existingCoverHash: string | undefined;
      if (existingCover) {
        if (!cover.hash) {
          localCoversAlreadyAvailable += 1;
          return;
        }
        existingCoverHash = (await calculateFileHash(existingCover)) as string;
        if (existingCoverHash === cover.hash && recoveredCoverUpdatedAt === undefined) {
          localCoversAlreadyAvailable += 1;
          return;
        }
        if ((localBook?.coverUpdatedAt || 0) > (recoveredCoverUpdatedAt ?? remoteEntry.meta?.coverUpdatedAt ?? 0)) {
          localCoversAlreadyAvailable += 1;
          logger.info('Google Drive sync kept a newer local library cover pending upload', {
            bookId,
            localCoverUpdatedAt: localBook?.coverUpdatedAt,
            remoteCoverUpdatedAt: recoveredCoverUpdatedAt ?? remoteEntry.meta?.coverUpdatedAt ?? null,
          });
          return;
        }
        if (isUnsyncedLocalEpubReplacement(localBook, remoteEntry)) {
          localCoversAlreadyAvailable += 1;
          logger.info('Google Drive sync kept unsynced local cover for replaced EPUB', {
            bookId,
            title: localBook?.title || remoteEntry.meta?.title || null,
            localCoverHashPresent: Boolean(existingCoverHash),
            remoteCoverHashPresent: Boolean(cover.hash),
            localEpubHashPresent: Boolean(localBook?.hash),
            lastSyncedEpubHashPresent: Boolean(localBook?.cloud?.googleDrive?.hash),
            remoteEpubHashPresent: Boolean(getRemoteEpubHash(remoteEntry)),
          });
          return;
        }
        staleLocalCovers += 1;
      }

      logger.info('Downloading Google Drive cover for synced book', {
        bookId,
        title: remoteEntry.meta?.title || null,
        coverFileIdPresent: true,
        staleLocalCover: Boolean(existingCover),
        expectedHashPresent: Boolean(cover.hash),
        expectedSize: cover.size || null,
      });
      const coverBlob = existingCover && existingCoverHash === cover.hash
        ? existingCover
        : await downloadDriveBlob(cover.fileId);
      const downloadedHash = coverBlob === existingCover
        ? existingCoverHash!
        : (await calculateFileHash(coverBlob)) as string;
      if (cover.hash && downloadedHash !== cover.hash) {
        logger.warn('Google Drive cover hash did not match manifest; checking for an interrupted upload', {
          bookId,
          manifestHash: cover.hash,
          downloadedHash,
        });
        const recovered = await recoverRemoteCoverRef(bookId, remoteEntry, downloadedHash);
        cover = recovered.cover;
        recoveredCoverUpdatedAt = recovered.coverUpdatedAt;
      }
      if (recoveredCoverUpdatedAt !== undefined) {
        if (
          (localBook?.coverUpdatedAt || 0) > recoveredCoverUpdatedAt ||
          (remoteEntry.meta.coverImageHash && remoteEntry.meta.coverImageHash !== downloadedHash &&
            (remoteEntry.meta.coverUpdatedAt || 0) >= recoveredCoverUpdatedAt)
        ) {
          logger.info('Google Drive sync is waiting for image bytes matching a newer cover edit', {
            bookId,
            localCoverUpdatedAt: localBook?.coverUpdatedAt || null,
            expectedHash: remoteEntry.meta.coverImageHash,
            downloadedHash,
            recoveredCoverUpdatedAt,
          });
          return;
        }
      }
      if (coverBlob !== existingCover) {
        await saveFile(coverKey, coverBlob, 'google-drive-sync-cover-downloaded');
      }
      if (recoveredCoverUpdatedAt !== undefined) {
        books[bookId] = {
          ...books[bookId],
          coverImageHash: downloadedHash,
          coverUpdatedAt: recoveredCoverUpdatedAt,
        };
        logger.warn('Recovered Google Drive cover sidecar written after its stale manifest entry', {
          bookId,
          coverImageHash: downloadedHash,
          coverUpdatedAt: recoveredCoverUpdatedAt,
          repairedSize: cover.size || coverBlob.size,
        });
      }
      markBookCloudState(books, bookId, {
        coverFileId: cover.fileId,
        coverHash: downloadedHash,
        coverSize: coverBlob.size,
        coverDownloadedAt: Date.now(),
      });
      changedBooks = true;
      downloadedRemoteCovers += 1;
      downloadedCoverBytes += coverBlob.size;
      logger.debug('Downloaded Google Drive cover for synced book', {
        bookId,
        title: remoteEntry.meta?.title || null,
        size: coverBlob.size,
        type: coverBlob.type || null,
        downloadedRemoteCovers,
        totalRemoteCovers: coverEntries.length,
      });
    } catch (error) {
      if (isGoogleDriveReauthorizationRequiredError(error)) {
        throw error;
      }
      failedRemoteCoverDownloads += 1;
      logger.warn('Failed to download Google Drive cover for synced book', {
        bookId,
        title: remoteEntry.meta?.title || null,
        coverFileIdPresent: true,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  logger.debug('Google Drive sync: checking remote covers for local thumbnails', {
    remoteCoverCount: coverEntries.length,
    concurrency: DRIVE_COVER_DOWNLOAD_CONCURRENCY,
  });
  emitCoverProgress(true);
  const workerCount = Math.min(DRIVE_COVER_DOWNLOAD_CONCURRENCY, Math.max(1, coverEntries.length));
  const workers = Array.from({ length: workerCount }, async () => {
    while (nextCoverIndex < coverEntries.length) {
      const coverIndex = nextCoverIndex;
      nextCoverIndex += 1;
      try {
        await processCover(coverEntries[coverIndex]);
      } finally {
        processedCovers += 1;
        emitCoverProgress(false);
      }
    }
  });
  await Promise.all(workers);

  const summary: RemoteCoverDownloadSummary = {
    remoteCoversWithFiles: coverEntries.length,
    localCoversAlreadyAvailable,
    staleLocalCovers,
    downloadedRemoteCovers,
    failedRemoteCoverDownloads,
    downloadedCoverBytes,
    changedBooks,
  };
  logger.debug('Google Drive sync: remote cover thumbnail pass finished', summary);
  emitCoverProgress(true);
  return summary;
};

const applyRemoteManifest = async (
  remoteManifest: DriveSyncManifestV1 | null,
  reason: string,
  syncStartedAt: number,
  localLastSyncAt?: number,
  localDeletedGroups?: Record<string, DriveDeletedGroup>,
): Promise<void> => {
  if (!remoteManifest) {
    return;
  }

  const books = { ...getBooks() };
  let changedBooks = false;
  let removedBooks = 0;
  let mergedBooks = 0;
  let unchangedBooks = 0;
  let remoteBooksWithFiles = 0;
  let localFilesAlreadyAvailable = 0;
  let remoteNotesWithFiles = 0;
  let downloadedRemoteNotes = 0;
  let remoteParentPlacementsApplied = 0;
  let localParentPlacementsKept = 0;
  let staleLocalEpubsMarkedCloudOnly = 0;
  let repairedInvalidBookParents = 0;
  let coverSummary: RemoteCoverDownloadSummary | null = null;
  let mergedDriveGroups: GroupCollection | undefined;
  logger.debug('Google Drive sync: applying remote manifest', {
    remoteBookCount: Object.keys(remoteManifest.books || {}).length,
    remoteDeletedBookCount: Object.keys(remoteManifest.deletedBooks || {}).length,
    remoteDeletedGroupCount: Object.keys(remoteManifest.deletedGroups || {}).length,
    localDeletedGroupCount: Object.keys(localDeletedGroups || {}).length,
    remoteGeneratedAt: remoteManifest.generatedAt || null,
    localLastSyncAt: localLastSyncAt || null,
  });

  // Folder prefs are independent of individual book files. Apply them first so
  // one damaged or interrupted notes upload cannot prevent the library layout
  // from being restored on a newly installed device.
  const currentUser = getCurrentUser();
  if (currentUser && remoteManifest.prefs) {
    const { prefs, groupSummary } = mergePrefsForDrive(
      currentUser.prefs || {},
      remoteManifest.prefs,
      remoteManifest.generatedAt || 0,
      localLastSyncAt,
      localDeletedGroups,
      remoteManifest.deletedGroups || {},
    );
    mergedDriveGroups = prefs.groups;
    logger.debug('Google Drive sync: merged remote folder prefs before book files', {
      ...groupSummary,
      remoteGeneratedAt: remoteManifest.generatedAt || null,
      localLastSyncAt: localLastSyncAt || null,
      remoteAccountPrefsPreferred: !localLastSyncAt || (remoteManifest.generatedAt || 0) > localLastSyncAt,
      activeGroupId: prefs.activeGroupId || null,
    });
    if (!areJsonValuesEqual(currentUser.prefs || {}, prefs)) {
      updateCurrentUser(
        {
          ...currentUser,
          prefs,
        },
        DRIVE_SYNC_REMOTE_APPLY_REASON,
      );
    }
  }

  for (const [bookId, remoteEntry] of Object.entries(remoteManifest.books || {})) {
    const remoteDeletedAt = remoteEntry.deletedAt || remoteManifest.deletedBooks?.[bookId]?.deletedAt;
    const localBook = books[bookId];
    if (remoteDeletedAt && (!localBook || (localBook.timestamp || 0) <= remoteDeletedAt)) {
      if (localBook) {
        delete books[bookId];
        changedBooks = true;
        removedBooks += 1;
      }
      continue;
    }

    const localFileAvailable = await doesFileExist(bookId);
    if (remoteEntry.epub?.fileId) {
      remoteBooksWithFiles += 1;
    }
    if (localFileAvailable) {
      localFilesAlreadyAvailable += 1;
    }
    const remoteMeta = cloneBook(remoteEntry.meta);
    let verifiedRemoteNotesState: Partial<GoogleDriveBookState> | null = null;
    let remoteNotesSidecarMissing = false;
    if (remoteEntry.notes?.fileId) {
      remoteNotesWithFiles += 1;
      const notesResult = await downloadRemoteBookNotes(bookId, remoteEntry, localBook);
      const verifiedNotesRef = notesResult.repairedRef || remoteEntry.notes;
      if (notesResult.notes) {
        remoteMeta.notes = notesResult.notes;
      }
      remoteNotesSidecarMissing = notesResult.sidecarMissing;
      if (notesResult.downloaded) {
        downloadedRemoteNotes += 1;
      }
      if ((notesResult.verifiedRef || notesResult.localDeletion) && verifiedNotesRef) {
        verifiedRemoteNotesState = {
          notesFileId: verifiedNotesRef.fileId,
          notesHash: notesResult.notesHash || verifiedNotesRef.hash,
          notesSize: verifiedNotesRef.size,
          notesUploadedAt: verifiedNotesRef.uploadedAt,
        };
      }
    }
    remoteMeta.cloud = {
      ...remoteMeta.cloud,
      googleDrive: {
        provider: 'google-drive',
        ...remoteMeta.cloud?.googleDrive,
        fileId: remoteEntry.epub?.fileId || remoteMeta.cloud?.googleDrive?.fileId,
        coverFileId: remoteEntry.cover?.fileId || remoteMeta.cloud?.googleDrive?.coverFileId,
        notesFileId: remoteNotesSidecarMissing
          ? undefined
          : verifiedRemoteNotesState?.notesFileId ?? remoteMeta.cloud?.googleDrive?.notesFileId,
        hash: remoteEntry.epub?.hash || remoteMeta.hash,
        coverHash: remoteEntry.cover?.hash,
        notesHash: remoteNotesSidecarMissing
          ? undefined
          : verifiedRemoteNotesState?.notesHash ?? remoteMeta.cloud?.googleDrive?.notesHash,
        size: remoteEntry.epub?.size,
        coverSize: remoteEntry.cover?.size,
        notesSize: remoteNotesSidecarMissing
          ? undefined
          : verifiedRemoteNotesState?.notesSize ?? remoteMeta.cloud?.googleDrive?.notesSize,
        fileName: remoteEntry.epub?.name,
        mimeType: remoteEntry.epub?.mimeType,
        uploadedAt: remoteEntry.epub?.uploadedAt,
        notesUploadedAt: remoteNotesSidecarMissing
          ? undefined
          : verifiedRemoteNotesState?.notesUploadedAt ?? remoteMeta.cloud?.googleDrive?.notesUploadedAt,
        localFileAvailable,
        downloadedAt: localBook?.cloud?.googleDrive?.downloadedAt,
      },
    };

    const merged = mergeBookForDrive(localBook, remoteMeta);
    const remoteEpubHash = getRemoteEpubHash(remoteEntry);
    const localEpubHash = localBook?.hash;
    const remoteEpubDiffersFromLocal =
      Boolean(localBook && remoteEntry.epub?.fileId && remoteEpubHash && localEpubHash) &&
      remoteEpubHash !== localEpubHash;
    if (remoteEpubDiffersFromLocal && !isUnsyncedLocalEpubReplacement(localBook, remoteEntry)) {
      merged.hash = remoteEpubHash;
      merged.cloud = {
        ...merged.cloud,
        googleDrive: {
          provider: 'google-drive',
          ...merged.cloud?.googleDrive,
          ...remoteMeta.cloud?.googleDrive,
          localFileAvailable: false,
          downloadedAt: undefined,
        },
      };
      staleLocalEpubsMarkedCloudOnly += 1;
      logger.info('Google Drive sync kept newer remote EPUB over stale local file state', {
        bookId,
        title: merged.title || remoteEntry.meta?.title || null,
        localBookTimestamp: localBook?.timestamp || localBook?.lastUpdated || null,
        remoteBookTimestamp: remoteEntry.meta?.timestamp || remoteEntry.meta?.lastUpdated || null,
        localEpubHashPresent: Boolean(localEpubHash),
        lastSyncedEpubHashPresent: Boolean(localBook?.cloud?.googleDrive?.hash),
        remoteEpubHashPresent: Boolean(remoteEpubHash),
      });
    }
    const parentResolution = resolveBookParentIdForDrive(
      localBook,
      remoteMeta,
      merged,
      remoteManifest.generatedAt || 0,
      localLastSyncAt,
    );
    if (parentResolution.parentId !== merged.parentId || parentResolution.parentUpdatedAt !== merged.parentUpdatedAt) {
      merged.parentId = parentResolution.parentId;
      merged.parentUpdatedAt = parentResolution.parentUpdatedAt;
    }
    if (localBook && localBook.parentId !== remoteMeta.parentId) {
      if (parentResolution.source === 'remote') {
        remoteParentPlacementsApplied += 1;
      } else if (parentResolution.source === 'local') {
        localParentPlacementsKept += 1;
      }
    }
    if (JSON.stringify(localBook || null) !== JSON.stringify(merged)) {
      books[bookId] = merged;
      changedBooks = true;
      mergedBooks += 1;
    } else {
      unchangedBooks += 1;
    }
  }

  coverSummary = await downloadRemoteCovers(remoteManifest, books, reason, syncStartedAt);
  if (coverSummary.changedBooks) {
    changedBooks = true;
  }

  repairedInvalidBookParents = repairBookParentIdsForDrive(
    books,
    mergedDriveGroups,
    remoteManifest.generatedAt || Date.now(),
  );
  if (repairedInvalidBookParents > 0) {
    changedBooks = true;
    logger.debug('Google Drive sync: repaired book folder references after folder merge', {
      repairedInvalidBookParents,
      groupCount: Object.keys(mergedDriveGroups || {}).length,
    });
  }

  if (changedBooks) {
    updateBooksCache(books, DRIVE_SYNC_REMOTE_APPLY_REASON);
  }

  const currentFollowing = getFollowing();
  const mergedFollowing = mergeFollowing(currentFollowing, remoteManifest.following);
  if (mergedFollowing && !areJsonValuesEqual(currentFollowing || [], mergedFollowing)) {
    updateFollowing(mergedFollowing, DRIVE_SYNC_REMOTE_APPLY_REASON);
  }

  if (remoteManifest.misc) {
    const currentMisc = getMisc();
    const mergedMisc = {
      ...(remoteManifest.misc as any),
      ...(currentMisc || {}),
    };
    if (!areJsonValuesEqual(currentMisc, mergedMisc)) {
      updateMisc(mergedMisc, DRIVE_SYNC_REMOTE_APPLY_REASON);
    }
  }

  if (remoteManifest.notesStatus) {
    const currentNotesStatus = getNotesStatus();
    const mergedNotesStatus = mergeNotesStatus(currentNotesStatus, remoteManifest.notesStatus);
    if (!areJsonValuesEqual(currentNotesStatus, mergedNotesStatus)) {
      updateNotesStatus(mergedNotesStatus, DRIVE_SYNC_REMOTE_APPLY_REASON);
    }
  }
  logger.debug('Google Drive sync: applied remote manifest', {
    changedBooks,
    removedBooks,
    mergedBooks,
    unchangedBooks,
    remoteParentPlacementsApplied,
    localParentPlacementsKept,
    staleLocalEpubsMarkedCloudOnly,
    repairedInvalidBookParents,
    remoteBooksWithFiles,
    localFilesAlreadyAvailable,
    remoteNotesWithFiles,
    downloadedRemoteNotes,
    remoteCoversWithFiles: coverSummary?.remoteCoversWithFiles ?? 0,
    localCoversAlreadyAvailable: coverSummary?.localCoversAlreadyAvailable ?? 0,
    staleLocalCovers: coverSummary?.staleLocalCovers ?? 0,
    downloadedRemoteCovers: coverSummary?.downloadedRemoteCovers ?? 0,
    failedRemoteCoverDownloads: coverSummary?.failedRemoteCoverDownloads ?? 0,
    downloadedCoverBytes: coverSummary?.downloadedCoverBytes ?? 0,
    hasPrefs: Boolean(remoteManifest.prefs),
    hasFollowing: Boolean(remoteManifest.following),
    hasMisc: Boolean(remoteManifest.misc),
    hasNotesStatus: Boolean(remoteManifest.notesStatus),
  });
};

const applyLocalTombstones = async (
  manifest: DriveSyncManifestV1,
  remoteManifest: DriveSyncManifestV1 | null,
): Promise<AppliedLocalTombstones> => {
  const state = await getLocalDriveSyncState();
  const localDeletedBooks = state.deletedBooks || {};
  const localDeletedGroups = state.deletedGroups || {};
  const nextDeletedBooks = {
    ...(remoteManifest?.deletedBooks || {}),
    ...localDeletedBooks,
  };
  const nextDeletedGroups = mergeDeletedGroupsByNewest(localDeletedGroups, remoteManifest?.deletedGroups);

  logger.debug('Google Drive sync: applying local tombstones', {
    localTombstoneCount: Object.keys(localDeletedBooks).length,
    remoteTombstoneCount: Object.keys(remoteManifest?.deletedBooks || {}).length,
    localGroupTombstoneCount: Object.keys(localDeletedGroups).length,
    remoteGroupTombstoneCount: Object.keys(remoteManifest?.deletedGroups || {}).length,
  });
  for (const deletedBook of Object.values(localDeletedBooks)) {
    const remoteEntry = remoteManifest?.books?.[deletedBook.id];
    logger.debug('Google Drive sync: deleting tombstoned Drive files', {
      bookId: deletedBook.id,
      epubFileIdPresent: Boolean(deletedBook.fileId || remoteEntry?.epub?.fileId),
      coverFileIdPresent: Boolean(deletedBook.coverFileId || remoteEntry?.cover?.fileId),
      notesFileIdPresent: Boolean(deletedBook.notesFileId || remoteEntry?.notes?.fileId),
    });
    await deleteDriveFile(deletedBook.fileId || remoteEntry?.epub?.fileId);
    await deleteDriveFile(deletedBook.coverFileId || remoteEntry?.cover?.fileId);
    await deleteDriveFile(deletedBook.notesFileId || remoteEntry?.notes?.fileId);
    delete manifest.books[deletedBook.id];
    nextDeletedBooks[deletedBook.id] = deletedBook;
  }

  manifest.deletedBooks = nextDeletedBooks;
  if (manifest.prefs?.groups) {
    for (const [groupId, deletedGroup] of Object.entries(nextDeletedGroups)) {
      if (groupId === 'root') {
        continue;
      }
      const group = manifest.prefs.groups[groupId];
      const groupTimestamp = getSingleGroupTimestamp(group);
      const deletedAt = toNumber(deletedGroup.deletedAt) || 0;
      if (group && groupTimestamp > deletedAt) {
        delete nextDeletedGroups[groupId];
        continue;
      }
      delete manifest.prefs.groups[groupId];
    }
    if (manifest.prefs.activeGroupId && !manifest.prefs.groups[manifest.prefs.activeGroupId]) {
      manifest.prefs.activeGroupId = getRootGroupId(manifest.prefs.groups);
    }
  }
  if (Object.keys(nextDeletedGroups).length > 0) {
    manifest.deletedGroups = nextDeletedGroups;
  } else {
    delete manifest.deletedGroups;
  }
  logger.debug('Google Drive sync: applied local tombstones', {
    appliedCount: Object.keys(localDeletedBooks).length,
    deletedBookCount: Object.keys(nextDeletedBooks).length,
    appliedGroupCount: Object.keys(localDeletedGroups).length,
    deletedGroupCount: Object.keys(nextDeletedGroups).length,
  });
  return {
    manifest,
    deletedBooks: localDeletedBooks,
    deletedGroups: localDeletedGroups,
  };
};

const clearAppliedLocalTombstones = async (applied: AppliedLocalTombstones): Promise<void> => {
  const appliedBookEntries = Object.entries(applied.deletedBooks);
  const appliedGroupEntries = Object.entries(applied.deletedGroups);
  if (appliedBookEntries.length === 0 && appliedGroupEntries.length === 0) {
    return;
  }

  await updateLocalDriveSyncState((current) => ({
    ...current,
    deletedBooks: appliedBookEntries.reduce(
      (deletedBooks, [bookId, tombstone]) => {
        if (deletedBooks[bookId]?.deletedAt !== tombstone.deletedAt) {
          return deletedBooks;
        }
        const nextDeletedBooks = { ...deletedBooks };
        delete nextDeletedBooks[bookId];
        return nextDeletedBooks;
      },
      current.deletedBooks || {},
    ),
    deletedGroups: appliedGroupEntries.reduce(
      (deletedGroups, [groupId, tombstone]) => {
        if (deletedGroups[groupId]?.deletedAt !== tombstone.deletedAt) {
          return deletedGroups;
        }
        const nextDeletedGroups = { ...deletedGroups };
        delete nextDeletedGroups[groupId];
        return nextDeletedGroups;
      },
      current.deletedGroups || {},
    ),
  }));
  logger.debug('Google Drive sync: cleared persisted local tombstones', {
    appliedBookCount: appliedBookEntries.length,
    appliedGroupCount: appliedGroupEntries.length,
  });
};

const uploadManifest = async (
  manifest: DriveSyncManifestV1,
  existingFile: DriveListedFile | null,
): Promise<DriveSyncManifestV1> => {
  manifest.generatedAt = Date.now();
  const blob = new Blob([JSON.stringify(manifest, null, 2)], {
    type: 'application/json',
  });
  logger.debug('Google Drive sync: uploading manifest', {
    existingFileIdPresent: Boolean(existingFile?.id),
    bookCount: Object.keys(manifest.books || {}).length,
    deletedBookCount: Object.keys(manifest.deletedBooks || {}).length,
    deletedGroupCount: Object.keys(manifest.deletedGroups || {}).length,
    size: blob.size,
  });
  const uploaded = await uploadBlobToDrive(
    blob,
    MANIFEST_NAME,
    'application/json',
    existingFile?.id,
    {
      kind: 'manifest',
      version: String(manifest.version),
    },
  );
  logger.info('Uploaded Google Drive sync manifest', {
    fileId: uploaded.id,
    size: uploaded.size,
  });
  return manifest;
};

const getComparableManifest = (manifest: DriveSyncManifestV1): DriveSyncManifestV1 => {
  const comparable: DriveSyncManifestV1 = {
    ...manifest,
    generatedAt: 0,
    deviceId: '',
  };
  if (Object.keys(comparable.deletedGroups || {}).length === 0) {
    delete comparable.deletedGroups;
  }
  return comparable;
};

const isDriveManifestContentUnchanged = (
  localManifest: DriveSyncManifestV1,
  remoteManifest: DriveSyncManifestV1 | null,
): boolean => {
  if (!remoteManifest) {
    return false;
  }
  return areJsonValuesEqual(
    getComparableManifest(localManifest),
    getComparableManifest(remoteManifest),
  );
};

export const syncGoogleDriveNow = async (reason = 'manual'): Promise<DriveSyncManifestV1 | null> => {
  const runtimeState = getDriveSyncRuntimeState();
  if (runtimeState.syncInFlight) {
    const elapsedMs = Date.now() - runtimeState.lastSyncStartedAt;
    if (elapsedMs > DRIVE_STALE_SYNC_IN_FLIGHT_MS) {
      logger.warn('Google Drive sync found stale in-flight sync; starting a new sync', {
        reason,
        elapsedMs,
        staleAfterMs: DRIVE_STALE_SYNC_IN_FLIGHT_MS,
      });
      runtimeState.syncInFlight = null;
    } else {
      logger.debug('Google Drive sync request joined existing in-flight sync', {
        reason,
        elapsedMs,
      });
      return runtimeState.syncInFlight;
    }
  }

  const now = Date.now();
  if (reason !== 'manual' && now - runtimeState.lastSyncStartedAt < MIN_SYNC_INTERVAL_MS) {
    logger.debug('Google Drive sync skipped by minimum interval', {
      reason,
      elapsedMs: now - runtimeState.lastSyncStartedAt,
      minimumMs: MIN_SYNC_INTERVAL_MS,
    });
    return null;
  }

  if (isDocumentHidden()) {
    logger.debug('Google Drive sync skipped because the app is hidden', {
      reason,
    });
    return null;
  }

  runtimeState.lastSyncStartedAt = Date.now();
  const syncStartedAt = runtimeState.lastSyncStartedAt;
  const syncPromise = Promise.resolve().then(async () => {
    dispatchDriveEvent('wereader:drive-sync-started', { reason });
    dispatchDriveSyncProgress({ reason, phase: 'starting', elapsedMs: 0 });
    try {
      logger.info('Google Drive sync started', {
        reason,
        startedAt: syncStartedAt,
      });
      dispatchDriveSyncProgress({ reason, phase: 'status', elapsedMs: Date.now() - syncStartedAt });
      const status = await getGoogleDriveStatus();
      logger.debug('Google Drive sync status checked', {
        reason,
        configured: status.configured,
        connected: status.connected,
        accountMatchesUser: normalizeEmail(status.email) === normalizeEmail(getCurrentUser()?.email),
        lastError: status.lastError || null,
      });
      if (!status.configured || !status.connected) {
        clearGoogleDriveAccessTokenCache();
        logger.info('Google Drive sync skipped because Drive is not connected', {
          reason,
          configured: status.configured,
          connected: status.connected,
        });
        return null;
      }

      const state = await getLocalDriveSyncState();
      logger.debug('Google Drive sync local state loaded', {
        reason,
        deviceId: state.deviceId,
        deletedBookCount: Object.keys(state.deletedBooks || {}).length,
        deletedGroupCount: Object.keys(state.deletedGroups || {}).length,
        lastSyncAt: state.lastSyncAt || null,
      });
      dispatchDriveSyncProgress({ reason, phase: 'remote-manifest', elapsedMs: Date.now() - syncStartedAt });
      const { manifest: remoteManifest, file: manifestFile } = await loadRemoteManifest();
      dispatchDriveSyncProgress({
        reason,
        phase: 'remote-apply',
        total: Object.keys(remoteManifest?.books || {}).length,
        elapsedMs: Date.now() - syncStartedAt,
      });
      await applyRemoteManifest(remoteManifest, reason, syncStartedAt, state.lastSyncAt, state.deletedGroups || {});

      logger.debug('Google Drive sync building outgoing manifest', {
        reason,
        remoteManifestPresent: Boolean(remoteManifest),
        remoteGeneratedAt: remoteManifest?.generatedAt || null,
        manifestFilePresent: Boolean(manifestFile?.id),
      });
      dispatchDriveSyncProgress({ reason, phase: 'local-manifest', elapsedMs: Date.now() - syncStartedAt });
      let nextManifest = await buildLocalManifest(state);
      nextManifest.deletedBooks = {
        ...(remoteManifest?.deletedBooks || {}),
        ...nextManifest.deletedBooks,
      };
      nextManifest = await uploadBookFiles(nextManifest, reason, syncStartedAt);
      dispatchDriveSyncProgress({
        reason,
        phase: 'tombstones',
        total: Object.keys(nextManifest.deletedBooks || {}).length,
        elapsedMs: Date.now() - syncStartedAt,
      });
      const appliedLocalTombstones = await applyLocalTombstones(nextManifest, remoteManifest);
      nextManifest = appliedLocalTombstones.manifest;
      dispatchDriveSyncProgress({
        reason,
        phase: 'manifest',
        total: Object.keys(nextManifest.books || {}).length,
        currentFileKind: 'manifest',
        elapsedMs: Date.now() - syncStartedAt,
      });
      const manifestUnchanged = isDriveManifestContentUnchanged(nextManifest, remoteManifest);
      const uploadedManifest = manifestUnchanged
        ? {
            ...nextManifest,
            generatedAt: remoteManifest?.generatedAt || nextManifest.generatedAt,
          }
        : await uploadManifest(nextManifest, manifestFile);
      if (manifestUnchanged && remoteManifest) {
        logger.info('Google Drive sync skipped manifest upload because content is unchanged', {
          reason,
          generatedAt: uploadedManifest.generatedAt,
          bookCount: Object.keys(uploadedManifest.books || {}).length,
          deletedBookCount: Object.keys(uploadedManifest.deletedBooks || {}).length,
          deletedGroupCount: Object.keys(uploadedManifest.deletedGroups || {}).length,
          elapsedMs: Date.now() - syncStartedAt,
        });
      }

      await clearAppliedLocalTombstones(appliedLocalTombstones);
      await updateLocalDriveSyncState({
        lastSyncAt: uploadedManifest.generatedAt,
        lastError: null,
      });
      logger.info('Google Drive sync complete', {
        reason,
        generatedAt: uploadedManifest.generatedAt,
        bookCount: Object.keys(uploadedManifest.books || {}).length,
        deletedBookCount: Object.keys(uploadedManifest.deletedBooks || {}).length,
        deletedGroupCount: Object.keys(uploadedManifest.deletedGroups || {}).length,
        elapsedMs: Date.now() - syncStartedAt,
      });
      dispatchDriveEvent('wereader:drive-sync-complete', {
        reason,
        generatedAt: uploadedManifest.generatedAt,
      });
      dispatchDriveSyncProgress({
        reason,
        phase: 'complete',
        total: Object.keys(uploadedManifest.books || {}).length,
        elapsedMs: Date.now() - syncStartedAt,
      });
      return uploadedManifest;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (isGoogleDriveSyncCancelledError(error)) {
        logger.info('Google Drive sync paused', {
          reason,
          cancelReason: error.reason,
          elapsedMs: Date.now() - syncStartedAt,
        });
        runtimeState.lastSyncStartedAt = 0;
        dispatchDriveSyncProgress({
          reason,
          phase: 'error',
          elapsedMs: Date.now() - syncStartedAt,
        });
        return null;
      }
      if (runtimeState.syncInFlight !== syncPromise) {
        logger.warn('Google Drive sync failed after a newer sync superseded it', {
          reason,
          error: message,
          elapsedMs: Date.now() - syncStartedAt,
        });
        return null;
      }
      runtimeState.lastSyncStartedAt = 0;
      logger.error('Google Drive sync failed', {
        reason,
        error: message,
        elapsedMs: Date.now() - syncStartedAt,
      });
      await updateLocalDriveSyncState({
        lastError: message,
      });
      dispatchDriveEvent('wereader:drive-sync-error', {
        reason,
        error: message,
      });
      dispatchDriveSyncProgress({
        reason,
        phase: 'error',
        elapsedMs: Date.now() - syncStartedAt,
      });
      throw error;
    } finally {
      if (runtimeState.syncInFlight === syncPromise) {
        runtimeState.syncInFlight = null;
        logger.debug('Google Drive sync cleared in-flight state', {
          reason,
          elapsedMs: Date.now() - syncStartedAt,
        });
      } else {
        logger.debug('Google Drive sync left newer in-flight state intact', {
          reason,
          elapsedMs: Date.now() - syncStartedAt,
        });
      }
    }
  });

  runtimeState.syncInFlight = syncPromise;
  return syncPromise;
};

export const downloadGoogleDriveBookFile = async (bookId: string): Promise<boolean> => {
  const book = getBooks()[bookId];
  const drive = book?.cloud?.googleDrive;
  if (!book || !drive?.fileId) {
    return false;
  }

  logger.info('Downloading EPUB from Google Drive app data', {
    bookId,
    title: book.title,
    fileId: drive.fileId,
  });
  const blob = await downloadDriveBlob(drive.fileId);
  const file = new File([blob], drive.fileName || `${book.title || bookId}.epub`, {
    type: drive.mimeType || blob.type || 'application/epub+zip',
    lastModified: Date.now(),
  });
  await saveFile(bookId, file);

  if (drive.coverFileId) {
    try {
      const coverKey = `cover-${bookId}`;
      const expectedHash = book.coverImageHash || drive.coverHash;
      const cachedCover = await getFile(coverKey);
      const cachedHash = cachedCover ? (await calculateFileHash(cachedCover)) as string : undefined;
      if (cachedCover && expectedHash && cachedHash === expectedHash) {
        logger.debug('Google Drive book download kept a verified library cover', {
          bookId,
          coverUpdatedAt: book.coverUpdatedAt || null,
          pendingLocalEdit: expectedHash !== drive.coverHash,
        });
      } else {
        logger.info('Google Drive book download is refreshing its library cover', {
          bookId,
          cachedHash: cachedHash || null,
          expectedHash: expectedHash || null,
          coverUpdatedAt: book.coverUpdatedAt || null,
        });
        const coverBlob = await downloadDriveBlob(drive.coverFileId);
        const downloadedHash = (await calculateFileHash(coverBlob)) as string;
        if (expectedHash && downloadedHash !== expectedHash) {
          logger.warn('Google Drive book cover does not match the current edit; keeping its cache', {
            bookId,
            expectedHash,
            downloadedHash,
          });
        } else {
          await saveFile(coverKey, coverBlob, 'google-drive-book-cover-downloaded');
        }
      }
    } catch (error) {
      logger.warn('Failed to download Google Drive cover; continuing with EPUB', {
        bookId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  const books = { ...getBooks() };
  markBookCloudState(books, bookId, {
    downloadedAt: Date.now(),
    localFileAvailable: true,
  });
  updateBooksCache(books);
  dispatchLocalDataChanged('drive-download');
  return true;
};
